/**
 * Compare to Thrive — the v37 `#reviewDupesModal` surface (subtask 06).
 *
 * A pre-write DRY RUN: it previews what `actionLabel` names — "Add to search"
 * or "Add to Thrive", chosen by the page — and flags the
 * warnings the recruiter must handle first. It writes NOTHING to Thrive.
 *
 * Structure (top → bottom), all in English, all design-system tokens (no inline
 * styles, no arbitrary `text-[Npx]`):
 *   - header: title + a plain-text subtitle (iter 3, 2026-07-07 — replaced
 *     both the ORANGE dry-run preamble box AND the iter-2 info-icon tooltip;
 *     one explanation, always visible, no popup, no redundant second copy),
 *     closed by a `CompareProgressDivider` (replaces the old plain `border-b`)
 *     showing the already-added fraction of the scope — plain hairline when
 *     nothing is exported (zero visual regression)
 *   - EMPTY-SCOPE state: when a manual selection filtered down to zero pending
 *     contacts (all already exported), a green "Nothing to compare" message +
 *     only Close (no dry-run poll started, no Continue)
 *   - stats block: "{actionLabel} will automatically:" (+ a "(X pending)"
 *     suffix only when the scope was narrowed) + the 4 canonical
 *     buckets (new / existing_add_to_search / linkedin_position_update /
 *     already_in_search)
 *   - THREE TABS over the conflicts (second design pass, 2026-09-08):
 *       · `Needs review` — ONE queue, ordered cheapest-to-close first (W2, then
 *         W3, then W1), with a `Can't be resolved here` divider marking where
 *         the recruiter stops being able to finish. It replaces three
 *         separately headed, separately paginated coloured group boxes, whose
 *         ranking was carried by their order and their colour rather than
 *         stated, and whose page 2 of the cheap work sat above page 1 of the
 *         expensive
 *       · `Different people` — the firm-wide 'not the same person' decisions
 *         and, for the first time, their Undo. Such a contact classifies NEW on
 *         the next compare and leaves `conflicts` entirely, so this list is
 *         read LIVE from the registry, not from the stored run
 *       · `Ignored` — the dimmed left-out cards with their Undo
 *     empty state → green "No conflicts" line
 *   - every card carries ONE verdict line and puts the rest behind an ⓘ; the
 *     grammar itself (left = your row, right = `In Thrive (N)`) lives in
 *     `ConflictCardShell`
 *   - footer: `Close` + `Continue to {actionLabel} →`, DISABLED while any
 *     conflict row is undecided, with a line beside it naming how many remain
 *     (fu5 sub-task 02 — every row ends the session answered by a human who
 *     looked at it). This gate is scoped to THIS button: Compare still never
 *     hard-blocks the page's own Export CTAs, per v37 / FR-13
 *
 * Presentational: it takes the `CompareResult` + loading flag + error +
 * callbacks. The polling/data orchestration lives in `useCompareToThrive`.
 *
 * SAFETY: a failed compare poll (network error, 5xx, timeout) must render an
 * explicit error state, NEVER the "No conflicts" success message — Compare's
 * whole job is to catch W1/W2 conflicts before Export runs, so silently
 * falling through to the empty-conflicts branch on a failed poll would be a
 * false-positive on the pre-export data-integrity safety gate.
 */

import type {
  CompareResult,
  ConflictContact,
  ImportContactRow,
  RecheckMode,
  W2Decision,
} from "@nu-assist/contracts";
import { Avatar, Button, ProgressBar, Spinner } from "@nu-assist/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { toast } from "sonner";
import type { CompareResultWithConflictActions, ImportsClient } from "../../clients/imports.client";
import { ImportsApiError } from "../../clients/imports.client";
import { thriveContactUrl } from "../../lib/thriveLinks";
import { InfoHint } from "../../routes/research/import-detail/InfoHint";
import { useResolveAvatars } from "../../routes/research/import-detail/useResolveAvatars";
import { useCompareGamification } from "../CompareGamificationProvider";
import { CleanupCelebration, celebrationDurationMs } from "./CleanupCelebration";
import { CompanyChangeRow } from "./CompanyChangeRow";
import { CompareProgressDivider } from "./CompareProgressDivider";
import { DifferentPersonCard, type RecheckOutcome } from "./DifferentPersonCard";
import { IgnoredCompanyChangeRow } from "./IgnoredCompanyChangeRow";
import { W1ConflictCard } from "./W1ConflictCard";
import { W2ConflictCard, conflictActionValues } from "./W2ConflictCard";
import { CompareBucketList } from "./compare-summary/CompareBucketList";
import {
  conflictQuestions,
  refreshNoticeLine,
  revalidateDecisionSet,
  revalidateDecisions,
} from "./compareRefresh";
import {
  ADDED_TO_SEARCH_LINE,
  ADDING_TO_SEARCH_LINE,
  ADD_FAILED_LINE,
  ADD_NEEDS_COMPARE_DETAIL,
  ADD_SKIPPED_LINE,
  ADD_STILL_RUNNING_LINE,
  COMPANY_CHANGE_DETAIL,
  COMPANY_CHANGE_LEAD,
  COMPANY_CHANGE_SECTION,
  CONFLICTS_HANDLED_FOR_NOW,
  CONFLICT_ACTION_FAILED,
  type ConflictAction,
  DIFFERENT_PEOPLE_SECTION,
  DONE_SECTION_TITLE,
  type DuplicateLinkedInAction,
  FIX_IN_THRIVE_SECTION,
  IGNORED_TAB_EMPTY,
  IGNORED_TAB_LABEL,
  LEFT_OUT_PILL,
  NEEDS_DECISION_SECTION,
  NEEDS_REVIEW_TAB_EMPTY,
  NEEDS_REVIEW_TAB_LABEL,
  PENDING_FILTER_ALL,
  RECHECK_CHECK_FAILED,
  RECHECK_STILL_COLLIDING,
  RERUN_COMPARE_LABEL,
  RERUN_COMPARE_RUNNING_LABEL,
  UNDO_DIFFERENT_PERSON_FAILED,
  UNDO_LEAVE_OUT_LABEL,
  UNDO_NEEDS_RECOMPARE_NOTE,
  UPDATED_IN_THRIVE_LINE,
  WELL_DONE_LINE,
  confirmAllLabel,
  confirmAllNeedsCompareLine,
  doneAddedToSearch,
  doneChangedRole,
  doneCleanedAndVerified,
  doneUpdated,
  dupWarningLine,
  dupWarningTitle,
  formatIgnoredDate,
  ignoredStatsLine,
  inheritedResultNote,
  priorTruncatedNote,
  undecidedBlockerLine,
} from "./conflictActionsCopy";
import { compareProgressPhaseLabel } from "./preCompareCopy";

export interface CompareToThriveModalProps {
  /** Imports API client — used to presign the W2 conflict contacts' stored
   *  avatars (`profile_image_s3_key`) so the cards render the durable S3 copy.
   *  Optional: when absent (e.g. in isolated component tests) the cards show
   *  initials for the source contact (the web has no licdn fallback). */
  api?: ImportsClient;
  /** The import the compare run belongs to. Required (together with `api`)
   *  for the persisted conflict actions ('Leave out' / 'Different person');
   *  when absent those two CTAs don't render. 'Decide later' is pure client
   *  view-state and needs neither. */
  importId?: string;
  /** The compare run (null while the first poll is in flight). */
  result: CompareResult | null;
  /** True while a compare run is queued/running (or the first poll is pending). */
  isRunning: boolean;
  /**
   * Set when the compare run (start or poll) failed. Takes priority over
   * `result` — see the SAFETY note above. Retry re-runs the dry run.
   */
  error?: Error | null;
  /** Close the modal, handing back the answers given in this sitting.
   *
   *  THE ARGUMENT IS LOAD-BEARING since the footer's primary action became the
   *  re-run (2026-09-14). While rows are still unanswered the modal no longer
   *  offers Continue — the recruiter who wants out mid-way closes it and uses
   *  the page's own `Add to search`, which skips whatever is still to-review.
   *  That exit silently DESTROYED their work until this: Compare persists
   *  nothing, and `onContinueToExport` used to be the only way the answers ever
   *  left the component.
   *
   *  Optional so the many isolated renders passing `() => {}` keep compiling; a
   *  caller that ignores it is choosing to drop the answers. */
  onClose: (w2Decisions?: Record<string, W2Decision>) => void;
  /**
   * the continue button ("Continue to {actionLabel} →"). Receives
   * the per-contact W2 decisions (request-time
   * only — Compare persists nothing). Always available.
   */
  onContinueToExport: (w2Decisions: Record<string, W2Decision>) => void;
  /** Retry a failed compare run (re-invokes `start`). Optional — the retry
   * button only renders when provided. */
  onRetry?: () => void;
  /**
   * Contacts in the current compare scope already exported to Thrive — drives
   * the header progress divider (and the "(X pending)" stats suffix when > 0).
   * Caller-computed: `exported_count` for the whole import, or the count of
   * already-exported rows within a manual selection. Default 0 → the divider
   * renders as today's plain hairline (zero visual regression).
   */
  alreadyExportedCount?: number;
  /**
   * Total contacts in the current compare scope (whole-import `total_contacts`,
   * or the selection size). Denominator for the divider.
   */
  scopeTotal?: number;
  /** How many contacts the run will actually compare, when the CALLER can
   *  count it exactly. `undefined` falls back to `scopeTotal -
   *  alreadyExportedCount`, which over-states by the dismissed rows.
   *
   *  A number rather than a second count to subtract, deliberately. The run
   *  drops two sets and they come from different places — the export aggregate
   *  and the `review_status` partition — where `ignored` OUTRANKS `exported`,
   *  so a row that was exported and later dismissed appears in both and
   *  subtracting them separately counts it twice. The page knows which numbers
   *  share a partition; this component does not, and should not do arithmetic
   *  across sources it cannot see. */
  pendingOverride?: number;
  /**
   * Wording of the divider legend + empty-state copy: whole-import vs. a manual
   * row selection.
   */
  scope?: "import" | "selection";
  /**
   * True when a manual selection resolved to ZERO pending contacts after
   * filtering out already-exported rows. Renders the "Nothing to compare" empty
   * state (only Close, no Continue) instead of the dry-run tree — the poll is
   * not started in this case (see `autoStart` in `useCompareToThrive`).
   */
  nothingToCompare?: boolean;
  /**
   * True while the caller is resolving the compare scope before the dry-run can
   * start — only reachable on the whole-import + partial-export path, where the
   * full contact list is fetched once to narrow the dry-run to not-yet-exported
   * contacts. Renders a short "Preparing compare…" spinner instead of a flash of
   * empty buckets; the poll has not started yet.
   */
  /**
   * True when the whole-import narrowing fetch (the one-shot full contact list)
   * failed and its retries are exhausted. Renders an explicit error + Retry
   * instead of holding the "preparing" spinner forever — the dry-run never
   * started, so this is distinct from `error` (a failed compare poll). Only
   * Close + Retry are offered, no Continue.
   */
  /**
   * Retry the failed narrowing fetch (re-issues the contact-list request, NOT
   * the compare poll). Optional — the Retry button only renders when provided.
   */
  /**
   * True when the import is a Company Map (BCT) type. Changes bucket label copy
   * to drop "added to this/the search" language — BCT contacts are only created
   * in Thrive, never added to a search.
   */
  isBct?: boolean;
  /**
   * What the write this modal previews is CALLED — "Add to search" on a
   * candidate list, "Add to Thrive" on a Company Map.
   *
   * Supplied by the page from `addAction`, the one object that decides both the
   * name and the route. Until this prop existed the same two strings were
   * hand-kept literals here, and the comment at the continue button said so in
   * letters: rename `addAction.label` and nothing in this file went red.
   *
   * SAME PROP NAME AS `ExportToThrivePanel`, DIFFERENT FIELD. That component
   * takes `addAction.selectedLabel` ("Add Selected to Thrive") because it acts
   * on a selection; this one takes `addAction.label`, because a dry run
   * previews the write itself. Copy the panel's wiring into a third surface and
   * you get "Selected" in a context that has no selection.
   *
   * Defaults to the candidate-list wording so the component still stands alone.
   */
  actionLabel?: string;
}

/** The four PENDING sections, in the order they close cheapest-first. A named
 *  union rather than free strings: it is the filter-chip vocabulary AND the
 *  section identity, and the two must not be able to drift apart. */
type PendingSection = "company_change" | "needs_decision" | "different_people" | "cant_resolve";

/** Conflicts paginate at 10 per page (2026-07-07 operator feedback) — a
 * large W1/W2 batch otherwise turns the modal into an unbounded scroll. */
const CONFLICTS_PAGE_SIZE = 10;

/** Marks the one-time Undo explanation as seen. Session-scoped: it explains a
 *  mechanism, which is learned once, but a new session is a new reader. */
const UNDO_NOTE_SEEN_KEY = "nu-assist:compare:undo-recompare-note-seen";

function ConflictPager({
  page,
  total,
  onPageChange,
  itemLabel,
}: {
  page: number;
  total: number;
  onPageChange: (page: number) => void;
  itemLabel: string;
}) {
  const pageCount = Math.max(1, Math.ceil(total / CONFLICTS_PAGE_SIZE));
  if (pageCount <= 1) return null;
  const start = page * CONFLICTS_PAGE_SIZE + 1;
  const end = Math.min(total, start + CONFLICTS_PAGE_SIZE - 1);
  return (
    <div className="mt-3 flex items-center justify-between text-caption text-text-secondary">
      <span>
        Showing {start}–{end} of {total} {itemLabel}
      </span>
      <div className="flex gap-2">
        <Button variant="secondary" disabled={page === 0} onClick={() => onPageChange(page - 1)}>
          Previous
        </Button>
        <Button
          variant="secondary"
          disabled={page >= pageCount - 1}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

/** What `addOneAndReport` tells its caller. The server's four outcomes, plus
 *  `unavailable` (no client) and `needs_compare` (skipped for a Compare that is
 *  missing or too old, Trello #823), which `Confirm all` counts. */
type AddReport = "added" | "skipped" | "failed" | "pending" | "unavailable" | "needs_compare";

export function CompareToThriveModal({
  result,
  isRunning,
  error = null,
  onClose,
  onContinueToExport,
  onRetry,
  alreadyExportedCount = 0,
  scopeTotal = 0,
  pendingOverride,
  scope = "import",
  nothingToCompare = false,
  api,
  importId,
  isBct = false,
  actionLabel = "Add to search",
}: CompareToThriveModalProps) {
  // W2 per-contact decisions held as REQUEST-TIME state (default Skip). NOT
  // persisted by Compare — carried to the Export payload on Continue.
  const [w2Decisions, setW2Decisions] = useState<Record<string, W2Decision>>({});
  // --- Refresh (fu5 follow-up, operator 2026-09-14) -------------------------
  //
  // A re-run WITHOUT closing the modal, because the per-card re-check answers
  // one question and the fix that matters most changes the answer: "a Lee Park
  // le borro el linkedin url, luego matchear con otro Lee Park, pero eso no lo
  // veo hasta que vuelvo a hacer click en compare".
  //
  // `refreshFrom` holds the questions as they stood when Refresh was pressed
  // (null when no refresh is in flight). Snapshotting at the CLICK is the point
  // — by the time the new run lands, `result` is the new one and the old
  // question is gone, which is exactly the comparison that has to be made.
  const [refreshFrom, setRefreshFrom] = useState<Map<string, string> | null>(null);
  // The job the modal has already reconciled. A poll returns a new object every
  // tick, so identity cannot say "a new run landed"; the run's own id can.
  const reconciledJobRef = useRef<string | null>(null);
  const [refreshNotice, setRefreshNotice] = useState<string | null>(null);
  /** The error a refresh was requested FROM, so the reconciliation can tell it
   *  apart from one the refresh itself produced. See the effect below. */
  const refreshedFromErrorRef = useRef<unknown>(null);
  /**
   * Did a compare run VERIFY that nothing is left?
   *
   * The export CTA's only condition, and it is a fact about the last RUN rather
   * than about the screen (operator, 2026-09-15: "the only time it should
   * change is the first time when it verifies everything was fixed").
   *
   * Two earlier versions read the screen and both were wrong in the same
   * direction — they let the button announce a verification nobody performed.
   * `pendingTotal > 0` flipped when the recruiter ANSWERED rows, since an
   * answer does not settle anything server-side. `undecidedCount > 0` flipped
   * when the only work left was a kind that offers no answer at all: a W1
   * pile-up, a same-URL card, a pre-ticked company change. None of those is in
   * `needsDecisionItems`, so the count read 0 with the work still on screen.
   *
   * Starts false, including on the very first compare: until a run has come
   * back, "nothing is left" is not something we know.
   */
  const [verifiedClear, setVerifiedClear] = useState(false);
  /** The run `verifiedClear` was computed from. A poll returns a new object per
   *  tick, so identity cannot say "a new run landed"; the run's own id can. */
  const verifiedJobRef = useRef<string | null>(null);
  // ONE PAGER PER SECTION, and the sections all live in one scroll. A single
  // pager over the merged queue would put a page boundary through a section;
  // per-section pagers keep each section's HEADER AND COUNT permanently
  // visible, which is the property that matters — the risk the operator named
  // is finishing what you can see and hitting Continue without learning that
  // more was held elsewhere, and a visible `Needs your decision (7)` cannot do
  // that to you even when you are on page 1 of it.
  const [companyChangePage, setCompanyChangePage] = useState(0);
  const [needsDecisionPage, setNeedsDecisionPage] = useState(0);
  const [differentPeoplePage, setDifferentPeoplePage] = useState(0);
  const [cantResolvePage, setCantResolvePage] = useState(0);
  // One page per SECTION, mirroring the pending tab. A single shared page
  // number would move both lists at once and show "1–10 of 12" over a section
  // holding 3.
  // ROWS DISMISSED BEFORE THIS SESSION, and they need their own source.
  //
  // Everything else here derives from `result.conflicts`. These cannot: Compare
  // EXCLUDES a dismissed row from its scope on purpose (`compare_worker/
  // repository.py` — "the recruiter said no once, and neither run should ask
  // again"), so a row ignored in an earlier session is absent from the next
  // run's conflicts, and `dedup_status` is overwritten each run. Deriving the
  // tab from the compare result showed `Ignored (0)` over a dismissal sitting
  // in the database — operator, 2026-09-08, verified on import `d6e2fee5`.
  //
  // KEPT IN `ImportContactRow`, NOT cast to `ConflictContact`. The two are
  // different shapes and the cast would have compiled: `ImportContactRow`
  // carries no `names_match` and no Thrive-side company, so a cast row would
  // render with no employer transition and would classify arbitrarily. The
  // conflict facts are not persisted per contact, so this is all the server
  // HAS for a row it did not compare — and the honest thing is to render less
  // rather than to fabricate. Restoring the full card is a follow-up that
  // needs those facts persisted; see the epic PRD.
  // The page's contact queries feed the COMPARE SCOPE (`ignoredIds`), so a
  // dismissal changed here has to reach them or the next run is computed from
  // stale data. See `refreshContactCaches`.
  const queryClient = useQueryClient();
  // Session-scoped so reopening the modal does not replay the explanation.
  // `sessionStorage` can THROW (private windows, blocked site data), and a note
  // is never worth breaking the modal for — so both ends are guarded and the
  // failure mode is simply showing it again.
  const [undoNoteShown, setUndoNoteShown] = useState(() => {
    try {
      return sessionStorage.getItem(UNDO_NOTE_SEEN_KEY) === "1";
    } catch {
      return false;
    }
  });
  // Whether to render it RIGHT NOW, as opposed to `undoNoteShown` which
  // records that this session has already had its one showing. Two flags
  // because the second outlives the modal and the first must not.
  const [showUndoNote, setShowUndoNote] = useState(false);
  const [priorIgnored, setPriorIgnored] = useState<ImportContactRow[]>([]);
  // The server's OWN total, not `priorIgnored.length`. The fetch takes one page
  // of 100 (the endpoint's cap), so on an import with more dismissals than that
  // the list is short — and a count derived from the list would under-report
  // silently, which is the same defect as the `(0)` this section fixes, just
  // rarer. Reachable today: the contact cap is 500, and card #759 raises it to
  // 5,000.
  const [priorIgnoredTotal, setPriorIgnoredTotal] = useState(0);
  // No page state of its own: the prior rows ride the SAME page as the cards
  // above them, because they are now the same section. A second pager inside
  // one section would be two controls over one list.
  const [ignoredCompanyChangePage, setIgnoredCompanyChangePage] = useState(0);
  const [ignoredDecisionPage, setIgnoredDecisionPage] = useState(0);
  // Its own page, like every other section: the Skipped tab's third section
  // holds a different card kind and must not move when its neighbours page.
  const [skippedDifferentPeoplePage, setSkippedDifferentPeoplePage] = useState(0);
  // TWO tabs. Everything PENDING is in one scroll under `needs_review`;
  // `ignored` is the only thing behind a second tab, and it is genuine archive.
  //
  // Tabs are wrong for hiding work and fine for archive (operator, 2026-09-08),
  // and the previous revision of this file had three — one of them holding the
  // 'different people' rows, which are decided but NOT finished: the two
  // records still share a URL and a human has to fix that. So those moved into
  // the pending scroll, and the tab they came from is gone.
  const [activeTab, setActiveTab] = useState<"needs_review" | "ignored">("needs_review");
  // Which pending section the chips narrow to. `all` is the default and is not
  // a view state anyone has to discover — the chips NARROW a long list, they
  // never hide a section that would otherwise render.
  const [pendingFilter, setPendingFilter] = useState<PendingSection | "all">("all");
  // Company-change rows the recruiter UNCHECKED. An exclusion set rather than
  // an inclusion one, because the section is pre-confirmed: storing the
  // confirmed ids would mean writing every row into state on mount just to
  // represent "unchanged".
  const [unconfirmedCompanyChanges, setUnconfirmedCompanyChanges] = useState<Set<string>>(
    () => new Set()
  );
  // Cards resolved IN THIS VIEW — COMPONENT STATE only (operator-approved
  // matrix): no server call beyond the action's own, no localStorage; a
  // reload, navigation away, or a new compare run re-raises anything still
  // conflicted. Since conflict-actions v2 the set is fed ONLY by a
  // successful 'Not the same person' (the card leaves the view because the
  // contact classifies NEW on the next compare). The old 'Decide later' CTA
  // that also fed it is gone entirely since fu5 sub-task 02: there is no
  // deferral arm on a conflict card any more, so nothing leaves this view
  // except a real answer.
  // Per-contact inline error for the two PERSISTED actions — a silent no-op
  // on a radio-looking control reads as a broken control (#838 iter-1, C-3).
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});
  const [dismissedInView, setDismissedInView] = useState<Set<string>>(() => new Set());
  // 'Leave out' — optimistic overrides layered on the server truth
  // (`result.left_out_contact_ids`, read live at poll time). key: contact id.
  const [leftOutOverrides, setLeftOutOverrides] = useState<Record<string, boolean>>({});
  // Registry pairs retired in THIS view, keyed `<contactId>:<thriveId>`. The
  // Different people list is derived live from the registry on every poll, so
  // this only covers the gap until the next one — a failed delete never lands
  // here, and the card comes back on its own.
  const [undoneInView, setUndoneInView] = useState<Set<string>>(() => new Set());
  const [undoErrors, setUndoErrors] = useState<Record<string, string>>({});
  // --- The duplicate-LinkedIn cleanup check (fu5 sub-task 02, Part B) -------
  //
  // Three pieces of state, and they are three because the outcomes are three.
  //   · `duplicateDecisions` — the ANSWER, written only when there is one. A
  //     `cleared` verdict decides the row; `still_colliding` and `check_failed`
  //     both leave it UNDECIDED, which is the honest state for "you have not
  //     answered this yet".
  //   · `recheckPendingIds`  — in flight. ONE THRIVE READ PER CLICK: the option
  //     goes disabled while this holds the id, and the handler refuses a second
  //     entry. No fan-out, no polling, no retry loop.
  //   · `recheckOutcomes`    — the last verdict per row, which is what drives
  //     the shake, the celebration and the "we couldn't check" line.
  const [duplicateDecisions, setDuplicateDecisions] = useState<
    Record<string, DuplicateLinkedInAction>
  >({});
  const [recheckPendingIds, setRecheckPendingIds] = useState<Set<string>>(() => new Set());
  const [recheckOutcomes, setRecheckOutcomes] = useState<
    Record<string, { outcome: RecheckOutcome; detail: string | null; attempt: number }>
  >({});
  // WHY A COUNTER AND NOT JUST THE OUTCOME. A CSS animation restarts only when
  // the class APPEARS on a node. Two `still_colliding` answers in a row leave
  // the class exactly as it was, so the second produces no motion — the very
  // case the shake exists for, since a recruiter who did not fix Thrive clicks
  // again. Dropping the verdict before re-asking (see the handler) was the
  // first attempt at this and is not enough: against the local API the delete
  // and the new value land in the same frame, and a class removed and re-added
  // with no repaint between them never restarts the animation.
  //
  // This counter changes on EVERY answer, and rides down to the shaking node as
  // its `key`, so React remounts it and the browser runs the keyframe again.
  // Monotonic across all rows on purpose: it only has to differ from its own
  // previous value, and one ref is cheaper to reason about than a map.
  const recheckAttemptRef = useRef(0);
  // Rows whose celebration has finished. See the filter that reads this for why
  // it is view-only, and `CleanupCelebration`'s docstring for why the row does
  // NOT get added to a search on its way out.
  const [celebratedIds, setCelebratedIds] = useState<Set<string>>(() => new Set());
  /**
   * WHAT THE RECRUITER ACHIEVED, kept on screen.
   *
   * Retiring a resolved row is right and, on its own, erases the work at the
   * moment it succeeds — twenty conflicts answered and an empty modal to show
   * for it (operator, 2026-09-11). Each landed write leaves a line here instead.
   *
   * VIEW-ONLY and session-scoped, like `celebratedIds`: it is a record of what
   * this sitting did, not a second source of truth about the import. Closing
   * the modal forgets it, and the contacts table is where the durable state
   * lives.
   */
  const [doneLog, setDoneLog] = useState<Array<{ id: string; line: string }>>([]);
  const noteDone = useCallback((id: string, line: string) => {
    setDoneLog((prev) => (prev.some((e) => e.id === id) ? prev : [...prev, { id, line }]));
  }, []);

  /** Press Refresh: remember what was being asked, then re-run. */
  const handleRefresh = useCallback(() => {
    if (!onRetry || isRunning) return;
    setRefreshFrom(conflictQuestions(result));
    refreshedFromErrorRef.current = error ?? null;
    // CLAIM THE RUN ON SCREEN AS ALREADY RECONCILED. `isRunning` does not go
    // true until the POST lands, so without this the effect below fires on the
    // very next render — against the OLD result, which reconciles the refresh
    // with itself and then ignores the run the recruiter actually asked for.
    reconciledJobRef.current = (result as { job_id?: string } | null)?.job_id ?? null;
    setRefreshNotice(null);
    onRetry();
  }, [onRetry, isRunning, result, error]);

  /** How many rows the last `Confirm all` left out for want of a recent
   *  Compare (Trello #823). Said once, at the end of the pass. */
  const [confirmAllNeedsCompareCount, setConfirmAllNeedsCompareCount] = useState(0);

  // Reconcile the modal with a run the recruiter asked for.
  //
  // EVERY OPTIMISTIC OVERLAY GOES. Each one exists only to cover the gap
  // between an action and the next server read, and this IS that read —
  // `dismissedInView` says so in as many words ("a reload, navigation away, or
  // a new compare run re-raises anything still conflicted"). Keeping them would
  // hide exactly what the refresh was pressed to reveal: the row that came back
  // as a different conflict.
  //
  // `doneLog` is the one that stays. It is not a claim about the import, it is
  // the record of what this sitting achieved ("twenty conflicts answered and an
  // empty modal to show for it"), and a refresh is the same sitting.
  //
  // The unexported answers are the only thing that cannot simply be re-read, so
  // they are the only thing revalidated — per the operator's own reading of the
  // state model, everything else either persisted on the spot or is a
  // to-review row the new run re-asks about anyway.
  // `w2Decisions` / `unconfirmedCompanyChanges` are READ below, never depended
  // on: listing them would re-run this effect on the very set it just wrote.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    if (refreshFrom === null) return;
    // A refresh that FAILED has no verdict to reconcile against, and leaving it
    // pending would let some later unrelated run reconcile against a snapshot
    // taken minutes ago.
    //
    // ONLY A **NEW** ERROR ABANDONS IT. The banner's own Retry is pressed while
    // `error` is still set — it is the error being retried — so a bare
    // `if (error)` threw the snapshot away on the very next render and the
    // retry reconciled nothing: overlays stale, decisions never revalidated
    // (/pr-review iter 2, Agent C, C2). Comparing identity separates "the run I
    // just asked for failed" from "the failure I am retrying".
    if (error && error !== refreshedFromErrorRef.current) {
      setRefreshFrom(null);
      refreshedFromErrorRef.current = null;
      return;
    }
    const jobId = (result as { job_id?: string } | null)?.job_id;
    // Wait for a TERMINAL run that is not the one already on screen. Without
    // the job check the in-flight `running` document (same shape, empty
    // conflicts) would reconcile against an empty question map and clear every
    // decision the recruiter had made.
    if (!jobId || isRunning || jobId === reconciledJobRef.current) return;
    reconciledJobRef.current = jobId;

    const after = conflictQuestions(result);
    const decisions = revalidateDecisions(w2Decisions, refreshFrom, after);
    const unchecked = revalidateDecisionSet(unconfirmedCompanyChanges, refreshFrom, after);
    setW2Decisions(decisions.kept);
    setUnconfirmedCompanyChanges(unchecked.kept);

    setDismissedInView(new Set());
    setLeftOutOverrides({});
    setUndoneInView(new Set());
    setCelebratedIds(new Set());
    setRecheckOutcomes({});
    setAddOutcomes({});
    // The note asked for this re-run, so it is answered now (/pr-review
    // #1109 iter 3, C I1).
    setConfirmAllNeedsCompareCount(0);
    setActionErrors({});
    setUndoErrors({});

    // One count for one message: a contact whose answer AND checkbox both went
    // is one row the recruiter has to revisit, not two.
    const cleared = new Set([...decisions.clearedIds, ...unchecked.clearedIds]);
    setRefreshNotice(refreshNoticeLine(cleared.size));
    setRefreshFrom(null);
    refreshedFromErrorRef.current = null;
  }, [refreshFrom, result, isRunning, error]);
  // `useCallback` because it is passed to `CleanupCelebration`, whose effect
  // lists it as a dependency — a new identity per render would restart the
  // burst's timer on every parent update and the row would never retire.
  const retireCelebratedRow = useCallback((contactId: string) => {
    setCelebratedIds((prev) => new Set(prev).add(contactId));
  }, []);
  //: Rows whose synchronous add is in flight. The row stays visible and says so
  //: — it must not disappear on a promise, which is the whole reason the add is
  //: synchronous rather than a 202.
  const [addingIds, setAddingIds] = useState<Set<string>>(() => new Set());
  //: The outcome of the last add per row, for the two cases that are NOT a
  //: celebration: `pending` (still running, not an error) and `failed`.
  const [addOutcomes, setAddOutcomes] = useState<
    Record<
      string,
      {
        outcome: string;
        detail: string | null;
        updated?: boolean;
        celebrate?: boolean;
        magnitude?: number;
      }
    >
  >({});

  /**
   * A decision RESOLVED this row — write it to Thrive now, and report the truth.
   *
   * THE GOAL IS A COMPARE WITH NOTHING PENDING (operator, 2026-09-10): a
   * recruiter who answers every card should leave the modal with the work done,
   * not with a queue of decisions to be applied later by a batch they have to
   * remember to run.
   *
   * It runs the SAME export the batch runs — one contact through the ordinary
   * job — so nothing about the write is duplicated, and the later batch skips
   * this row by the idempotency-on-identity rule rather than writing twice.
   *
   * The row is retired ONLY on `added`. `pending` leaves it in place with a
   * line saying the job is still going, because it is; `failed` leaves it with
   * the reason. Retiring on anything else would be the 202 dishonesty wearing a
   * different hat.
   */
  const addOneAndReport = useCallback(
    async (
      contactId: string,
      opts: {
        /** The per-contact W2/W3 decision the write carries. */
        w2Decision?: string;
        /** CONFIRM an unverified name match first — a W3 names a candidate the
         *  worker left off the row, so without this the export creates. */
        confirmThriveId?: string | null;
        /** Did the row ALREADY point at a Thrive contact? Decides "Updated in
         *  Thrive" vs "Added to the search" — a create returns an id too, so
         *  the response cannot answer it. */
        matchedBefore?: boolean;
        /** The Completed line, written by the caller that has the contact. */
        doneLine?: string;
        /** Does this write earn the burst? Only a verified cleanup does. */
        celebrate?: boolean;
        /** How many duplicate records the fix collapsed. */
        magnitude?: number;
      } = {}
    ): Promise<AddReport> => {
      const {
        w2Decision,
        confirmThriveId,
        matchedBefore = false,
        doneLine,
        celebrate = false,
        magnitude = 1,
      } = opts;
      if (!api?.addOneContactToSearch || !importId) return "unavailable";
      let outcome: AddReport = "pending";
      setAddingIds((prev) => new Set(prev).add(contactId));
      setAddOutcomes((prev) => {
        const next = { ...prev };
        delete next[contactId];
        return next;
      });
      try {
        const result = await api.addOneContactToSearch(
          importId,
          contactId,
          w2Decision,
          confirmThriveId
        );
        if (result.outcome === "skipped" && result.skip_reason) {
          // Held back before any run: this contact's Compare is missing or
          // older than the window (Trello #823, sub-task 04). Nothing was
          // written. Its own value so `Confirm all` can count it, and the row
          // keeps its card with the way out rather than the worker's "declined".
          outcome = "needs_compare";
          setAddOutcomes((prev) => ({
            ...prev,
            [contactId]: { outcome: "failed", detail: ADD_NEEDS_COMPARE_DETAIL },
          }));
          return outcome;
        }
        outcome = result.outcome;
        setAddOutcomes((prev) => ({
          ...prev,
          [contactId]: {
            outcome: result.outcome,
            detail: result.detail ?? null,
            // WHICH WRITE it was — and the answer is NOT the response's
            // `contact_id_thrive`. A create returns one too (the id of the row
            // it just made), so keying on it called every write an update,
            // including the one that had just created somebody (found in the
            // browser, 2026-09-11).
            //
            // What separates them is whether the row ALREADY pointed at a
            // Thrive contact when the write began: `update_anyway` matched one,
            // and a merge's re-check persisted the survivor's id. A
            // different-person row cleared to zero holders points at nobody,
            // and creating it is exactly what "Added to the search" describes.
            updated: result.outcome === "added" && matchedBefore,
            celebrate,
            magnitude,
          },
        }));
      } catch (error) {
        // 409 = ALREADY EXPORTED, which is a SUCCESS wearing an error's status
        // code. `_start` refuses a selection whose every contact is already
        // exported, so the idempotency no-op never reaches the poll and never
        // comes back as `skipped` — found 2026-09-11 by re-adding the same
        // contact against the live stack. Reporting it as "still adding" (the
        // old catch-all) told the recruiter their work was in flight when it
        // was already done.
        // FOUR DIFFERENT 409s LIVE ON THIS ROUTE, and treating them alike
        // reported failures as successes (operator, 2026-09-12: a row showed
        // its green line and never left, because nothing had been written).
        // Only ONE of them means the work is done:
        //
        //   · "already exported"     → done, and the happiest outcome
        //   · "export in progress"   → NOT done; the previous row's job is
        //                              still in flight. This is the one that
        //                              broke `Confirm all`, which writes
        //                              back-to-back.
        //   · Company Map / no search → refusals about the LIST, not this row.
        //
        // Keyed on the detail rather than the status, because the status cannot
        // tell them apart.
        const alreadyExported =
          error instanceof ImportsApiError &&
          error.status === 409 &&
          (error.detail ?? "").toLowerCase().includes("already exported");
        if (alreadyExported) {
          outcome = "added";
          setAddOutcomes((prev) => ({
            ...prev,
            // The 409 path carries the same two facts as the happy one: a row
            // that was already exported is still an update, and it earns the
            // paper on exactly the same terms.
            [contactId]: { outcome: "added", detail: null, updated: matchedBefore, celebrate },
          }));
          // NO EARLY RETURN. It used to `return outcome` here, which skipped the
          // whole `outcome === "added"` block below — so this path wrote the
          // per-card status node and then neither logged the green line nor
          // retired the row. That is "retorna el mensaje de update pero no
          // desaparece" (operator, 2026-09-12), reported against three separate
          // contacts and read each time as a different bug.
          //
          // An outcome of `added` has ONE meaning and therefore one epilogue;
          // reaching it through the 409 does not make it a different outcome.
        } else {
          // Anything else is not a verdict about Thrive — including the other
          // three 409s. `pending` is the honest reading for the in-flight one (a
          // job IS running and this row will be picked up), and for the rest it
          // at least does not claim a write that never happened.
          //
          // An `else`, now that the branch above no longer returns: falling
          // through would overwrite the success it had just recorded.
          setAddOutcomes((prev) => ({
            ...prev,
            [contactId]: { outcome: "pending", detail: null },
          }));
        }
      } finally {
        setAddingIds((prev) => {
          const next = new Set(prev);
          next.delete(contactId);
          return next;
        });
      }
      // THE RETIREMENT LIVES HERE, with the code that knows the outcome.
      //
      // It used to ride on `CleanupCelebration`'s `onDone`, which made a row's
      // disappearance depend on a child's timer surviving StrictMode's double
      // mount, on the flag being on, and on the caller passing a stable
      // callback. Three ways for a written row to stay on screen, and two of
      // them actually happened. The celebration now only decorates; whether a
      // row is still pending work is not its business.
      //
      // The delay is what makes the sequence readable — the recruiter has to
      // SEE which row was written — and it matches the burst's length so the
      // paper is not cut off mid-air.
      if (outcome === "added") {
        // The line is written BEFORE the row goes, so the two are one movement:
        // the card leaves the queue and its achievement appears in the list.
        if (doneLine) {
          noteDone(contactId, doneLine);
          // A TOAST *AND* A LINE, not one or the other. The toast is the
          // acknowledgement at the moment of the click — it follows the eye,
          // which is on the card about to vanish. The line is the record, and
          // it is what is still there twenty conflicts later.
          toast.success(doneLine);
        }
        // THE PAUSE IS COMPUTED FROM THE SERIES, not guessed. It was a
        // hardcoded 2400 — calibrated back when every celebration was one
        // burst — while a three-round series ends at 2800ms and a four-round
        // one at 3500ms. So the two biggest celebrations, the ones a recruiter
        // earns by clearing the worst pile-ups, were exactly the ones whose
        // last burst got cut off by the row leaving (/pr-review iter 1).
        //
        // `celebrationDurationMs` is the component's own arithmetic, exported
        // rather than restated: the caller owns WHEN the row goes (a
        // disappearance must not depend on a child's timer surviving
        // StrictMode), but only the component knows HOW LONG its show takes.
        window.setTimeout(
          () => retireCelebratedRow(contactId),
          celebrationDurationMs(magnitude, { burst: celebrationsEnabled && celebrate })
        );
      }
      return outcome;
    },
    [api, importId, retireCelebratedRow, noteDone]
  );
  // `?gamification=off`, default ON since 2026-09-15 (Trello #790 reviewed; see
  // the execution first). The flag is read HERE, once, rather than inside the
  // card: the card should not learn which flag reveals a decoration, and this
  // is the only place the response's `cleared` branch is also in scope.
  const { celebrationsEnabled } = useCompareGamification();

  const serverLeftOut = useMemo(
    () => new Set((result as CompareResultWithConflictActions | null)?.left_out_contact_ids ?? []),
    [result]
  );
  // Ignored = the server says so (either the live `left_out_contact_ids` list
  // or the card's own live-derived `review_status` — one derivation, two
  // carriers) unless an optimistic override from THIS view is newer.
  const isLeftOut = (c: ConflictContact): boolean =>
    leftOutOverrides[c.import_contact_id] ??
    (serverLeftOut.has(c.import_contact_id) || c.review_status === "ignored");

  const setDecision = (importContactId: string, decision: W2Decision) => {
    setW2Decisions((prev) => ({ ...prev, [importContactId]: decision }));
  };

  /** Return a row to UNDECIDED by DELETING its key.
   *
   *  It used to be `setDecision(id, "skip")`, and that is the exact line this
   *  sub-task exists to remove: writing the wire default back was
   *  indistinguishable from never having answered, so "cleared" and "answered
   *  with the neutral option" were one value. Deleting the key is what makes
   *  the two different — and what lets `actionFor` return null honestly instead
   *  of falling through `?? "skip"`.
   *
   *  Both callers are actions that MOVE THE CARD OUT of the pending list
   *  (`leaveOut` files it under Skipped, `markDifferentPerson` retires it), so
   *  clearing here can never strand a visible row as undecided; what it
   *  prevents is a stale batched `update_anyway` riding to Export behind a
   *  card the recruiter has already dismissed (pr-review #838 iter-1, A-F2). */
  const clearDecision = (importContactId: string) => {
    setW2Decisions((prev) => {
      if (!(importContactId in prev)) return prev;
      const next = { ...prev };
      delete next[importContactId];
      return next;
    });
  };

  /** Tick / untick one company-change row. Stored as the EXCLUSION set, so the
   *  pre-confirmed default costs no state at all. */
  const setCompanyChangeConfirmed = (importContactId: string, confirmed: boolean) =>
    setUnconfirmedCompanyChanges((prev) => {
      const next = new Set(prev);
      if (confirmed) next.delete(importContactId);
      else next.add(importContactId);
      return next;
    });

  // The card's SELECTED action, derived (never stored twice), and NULLABLE.
  //
  // THE `?? "skip"` FALLBACK THAT USED TO LIVE HERE WAS THE NEUTRAL DEFAULT.
  // It read `w2Decisions[contactId] ?? "skip"` and mapped the fallthrough to
  // `doNothing`, which made a row nobody touched and a row deliberately left
  // alone the SAME VALUE — the whole neutral default expressed in one line of
  // TypeScript. Deleting the label without deleting this would have changed the
  // vocabulary and none of the behaviour (fu5 sub-task 02, and the single
  // highest-risk misreading of it).
  //
  // ABSENCE IS NOW UNDECIDED. `w2Decisions` holds explicit answers only: the
  // two request-time actions write it, and `clearDecision` deletes from it. The
  // two PERSISTED actions never appear here at all — a `removeFromList` row
  // leaves for the Skipped tab and a `notSamePerson` row leaves the view
  // entirely, so neither needs a client-side value to be "decided".
  //
  // The wire `"skip"` value is unreachable from the cards after this change. It
  // survives as the SERVER's default for rows the modal never saw, and — via
  // `ExportToThrivePanel`'s own dropdown — as an explicit per-run deferral
  // there. It is not the recruiter-facing `Skip`, which is the persisted
  // `leave-out` dismissal.
  const actionFor = (contactId: string): ConflictAction | null => {
    switch (w2Decisions[contactId]) {
      case "update_anyway":
        return "updateAnyway";
      case "add_as_new":
        return "addAsNew";
      default:
        return null;
    }
  };

  // ONE handler for the unified action control.
  //
  // `updateAnyway` / `addAsNew` still record the batched per-contact decision —
  // Continue carries it, and that stays true for anyone who reaches the export
  // panel — but since 2026-09-10 they ALSO write the contact immediately
  // (operator: the recruiter should leave Compare with nothing pending). The
  // two paths do not collide: the single add runs the ordinary job, and the
  // later batch resolves an already-exported row to SKIP by the
  // idempotency-on-identity rule rather than writing twice.
  //
  // `notSamePerson` deliberately does NOT add. It asserts an identity — that
  // this URL belongs to somebody else — and turning that into "and put them in
  // the search" would fold two decisions into one click (operator's scope,
  // 2026-09-10). The row classifies NEW from now on and rides the batch.
  //
  // `removeFromList` does not add either, for the obvious reason.
  const handleAction = (c: ConflictContact, action: ConflictAction) => {
    const id = c.import_contact_id;
    setActionError(id, null);
    switch (action) {
      case "updateAnyway":
        setDecision(id, "update_anyway");
        // The decision travels WITH the add, on the same field the batch uses,
        // so the worker's plan resolver sees exactly what it would have seen
        // from the export panel.
        // "Same person" is an update by definition: it names the matched row.
        // On a W3 the candidate has to be CONFIRMED first — the worker left it
        // off the row, so without this the export would create instead.
        void addOneAndReport(id, {
          w2Decision: "update_anyway",
          confirmThriveId: c.contact_id_thrive ?? null,
          matchedBefore: true,
          doneLine: doneUpdated(contactName(c)),
        });
        break;
      case "addAsNew":
        // Same batched mechanism as `updateAnyway` — a request-time decision,
        // NOT a persisted column. There is nothing durable to record: the cards
        // that offer this have no LinkedIn URL, which is exactly why the
        // firm-wide registry cannot hold the decision either. The card's ⓘ says
        // so rather than letting the recruiter assume otherwise.
        setDecision(id, "add_as_new");
        // "Add as new contact" is a create by definition.
        void addOneAndReport(id, {
          w2Decision: "add_as_new",
          matchedBefore: false,
          doneLine: doneAddedToSearch(contactName(c)),
        });
        break;
      case "removeFromList":
        leaveOut(id);
        break;
      case "notSamePerson":
        // The option only renders when the card has a candidate id.
        if (c.contact_id_thrive) markDifferentPerson(id, c.contact_id_thrive);
        break;
    }
  };

  const dismissInView = (contactId: string) => {
    setDismissedInView((prev) => {
      const next = new Set(prev);
      next.add(contactId);
      return next;
    });
  };

  const setActionError = (contactId: string, message: string | null) =>
    setActionErrors((prev) => {
      const next = { ...prev };
      if (message === null) delete next[contactId];
      else next[contactId] = message;
      return next;
    });

  const leaveOut = (contactId: string) => {
    if (!api || !importId) return;
    void api.leaveOut(importId, contactId).then(
      () => {
        setLeftOutOverrides((prev) => ({ ...prev, [contactId]: true }));
        // Clear any staged update_anyway: a Leave-out -> Undo round-trip must
        // not resurrect a stale batched decision (pr-review #838 iter-1).
        // DELETES the key rather than writing the wire default back — see
        // `clearDecision`.
        clearDecision(contactId);
      },
      () => setActionError(contactId, CONFLICT_ACTION_FAILED)
    );
  };

  /** Push a dismissal change into the page's contact caches.
   *
   *  WHY THIS IS NOT COSMETIC. `computeCompareScope` takes `ignoredIds` from
   *  those caches, and the compare worker drops dismissed rows server-side —
   *  so after an Undo the cached list still called the contact ignored and the
   *  NEXT COMPARE WAS ENQUEUED WITHOUT IT. The recruiter undid a dismissal, ran
   *  Compare, and the contact did not come back; a page refresh "fixed" it,
   *  which is the signature of a stale cache (operator, 2026-09-08).
   *
   *  The optimistic `leftOutOverrides` map only ever governed THIS modal's own
   *  rendering, so it could not have covered this: the scope is computed a
   *  layer up, from data this modal does not own.
   *
   *  The keys mirror the set `ImportDetailPage` invalidates when a compare
   *  reaches terminal — same caches, same prefix-match reasoning. */
  const refreshContactCaches = () => {
    if (!importId) return;
    for (const key of [
      ["imports", importId, "contacts"],
      ["import-contacts", importId],
      ["import-contacts-all", importId],
    ]) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  };

  const undoLeaveOut = (contactId: string) => {
    if (!api || !importId) return;
    setActionError(contactId, null);
    void api.undoLeaveOut(importId, contactId).then(
      () => {
        setLeftOutOverrides((prev) => ({ ...prev, [contactId]: false }));
        refreshContactCaches();
        if (!undoNoteShown) {
          setUndoNoteShown(true);
          setShowUndoNote(true);
          try {
            sessionStorage.setItem(UNDO_NOTE_SEEN_KEY, "1");
          } catch {
            // Nothing to do: the note shows again next session, which is a
            // strictly better failure than a modal that cannot undo.
          }
        }
      },
      () => setActionError(contactId, CONFLICT_ACTION_FAILED)
    );
  };

  const markDifferentPerson = (contactId: string, thriveId: string) => {
    if (!api || !importId) return;
    void api.markDifferentPerson(importId, contactId, thriveId).then(
      // Resolved: the pair is registered firm-wide; this contact classifies
      // NEW on the next compare, so the conflict card leaves the view. Clear
      // any staged update_anyway so the dismissed card cannot ride a stale
      // batched decision to Export (pr-review #838 iter-1, A-F2).
      () => {
        clearDecision(contactId);
        dismissInView(contactId);
      },
      () => setActionError(contactId, CONFLICT_ACTION_FAILED)
    );
  };

  // Undo a firm-wide registry decision. Optimistic like the leave-out undo
  // beside it: the card leaves the Different people tab immediately, and the
  // next poll confirms it (the list is derived live from the registry, so a
  // failed delete brings the card straight back).
  const undoDifferentPerson = (contactId: string, thriveId: string) => {
    if (!api || !importId) return;
    setUndoErrors((prev) => {
      const next = { ...prev };
      delete next[contactId];
      return next;
    });
    void api.undoDifferentPerson(importId, contactId, thriveId).then(
      () =>
        setUndoneInView((prev) => {
          const next = new Set(prev);
          next.add(`${contactId}:${thriveId}`);
          return next;
        }),
      () =>
        setUndoErrors((prev) => ({
          ...prev,
          [contactId]: UNDO_DIFFERENT_PERSON_FAILED,
        }))
    );
  };

  /** Mark one duplicate-LinkedIn row's re-check as no longer in flight. */
  const clearRecheckPending = (contactId: string) =>
    setRecheckPendingIds((prev) => {
      if (!prev.has(contactId)) return prev;
      const next = new Set(prev);
      next.delete(contactId);
      return next;
    });

  /**
   * THE CLEANUP CHECK — the two options on a duplicate-LinkedIn card.
   *
   * `skipForNow` IS THE PERSISTED DISMISSAL. It routes through the same
   * `leaveOut` the conflict cards use (`PUT .../leave-out`, stored
   * `review_status = "ignored"`, undoable, filed under the Skipped tab). It is
   * NOT the wire `W2Decision` value `"skip"`, which is a per-run deferral that
   * re-raises on the next compare — wiring it there would silently downgrade a
   * durable decision into a deferral, and it is the second-highest-risk
   * misreading in this sub-task.
   *
   * `fixedInThrive` RE-QUERIES THRIVE AND BRANCHES ON THE ANSWER, never on the
   * click. The three outcomes are genuinely three:
   *
   *   cleared         → the row re-renders in its new state, and the
   *                     celebration fires FROM THE RESPONSE (flag permitting).
   *                     A celebration wired to this handler instead would be a
   *                     participation trophy and would be dishonest the first
   *                     time somebody misremembered fixing Thrive.
   *   still_colliding → the card shakes + a toast; THE ROW STAYS UNDECIDED.
   *                     This is the more valuable half, and it is not about
   *                     catching liars: it is the recruiter who genuinely
   *                     believed they had fixed it finding out now, on the row,
   *                     instead of at export time with the run half done.
   *   check_failed    → *unknown*. NO SHAKE, no celebration, row still
   *                     undecided. Deliberately a 200 from the server so it
   *                     does not vanish into the client's generic error path.
   *
   * BRANCHING ON `resolved` INSTEAD WOULD BE A BUG: it is false for BOTH
   * `still_colliding` and `check_failed`, so it cannot tell them apart, and the
   * cost of confusing them is shaking a card at somebody because Thrive was
   * rate-limited.
   */
  const handleDuplicateLinkedInAction = (
    contactId: string,
    action: DuplicateLinkedInAction,
    // WHICH question the card is asking. Defaulted to the stricter rule so a
    // call site that forgets it under-reports success rather than blessing a
    // duplicate create — see the server's `RecheckMode`.
    mode: RecheckMode = "different_person"
  ) => {
    if (recheckPendingIds.has(contactId)) return;
    if (action === "skipForNow") {
      setDuplicateDecisions((prev) => ({ ...prev, [contactId]: "skipForNow" }));
      leaveOut(contactId);
      return;
    }
    // `recheckDuplicateLinkedIn` is feature-detected, not just the client:
    // several suites pass a partial `ImportsClient` stubbing only what they
    // exercise, and the other calls here are guarded the same way.
    if (!api?.recheckDuplicateLinkedIn || !importId) return;
    // Drop the previous verdict FIRST. This was once the whole replay
    // mechanism and was not enough — against the local API the delete and the
    // new value land in the same frame. The wobble now replays off `attempt`
    // (see the counter above), and this stays for a different reason: a row
    // showing last run's verdict while the next read is in flight is stale.
    setRecheckOutcomes((prev) => {
      if (!(contactId in prev)) return prev;
      const next = { ...prev };
      delete next[contactId];
      return next;
    });
    setRecheckPendingIds((prev) => new Set(prev).add(contactId));
    void api.recheckDuplicateLinkedIn(importId, contactId, mode).then(
      (verdict) => {
        clearRecheckPending(contactId);
        setRecheckOutcomes((prev) => ({
          ...prev,
          [contactId]: {
            outcome: verdict.outcome,
            detail: verdict.detail ?? null,
            attempt: ++recheckAttemptRef.current,
          },
        }));
        switch (verdict.outcome) {
          case "cleared":
            setDuplicateDecisions((prev) => ({ ...prev, [contactId]: "fixedInThrive" }));
            // The collision is verifiably gone, so the row is addable NOW.
            // Fired from the VERDICT, like everything else on this path: the
            // recruiter's click asked a question, and this is the answer being
            // acted on.
            // The re-check's own verdict carries the survivor's id when a
            // merge left one — that row is an UPDATE. Zero holders is a create.
            void addOneAndReport(contactId, {
              matchedBefore: Boolean(verdict.contact_id_thrive),
              // The two cleanup paths read differently on purpose: a merge is
              // work done in Thrive and verified here, a removed URL frees the
              // row to be created.
              doneLine: verdict.contact_id_thrive
                ? doneCleanedAndVerified(nameOf(contactId))
                : doneAddedToSearch(nameOf(contactId)),
              celebrate: true,
              magnitude: cleanedCountFor(contactId),
            });
            break;
          case "still_colliding":
            // `warning`, not `error`: nothing failed. We asked Thrive a
            // question and the answer was no.
            toast.warning(RECHECK_STILL_COLLIDING);
            break;
          case "check_failed":
            // The server's own recruiter-safe line when it sent one. Neutral
            // tone — this is not the recruiter's doing.
            toast.message(verdict.detail || RECHECK_CHECK_FAILED);
            break;
        }
      },
      () => {
        // A genuine transport/4xx failure (404, 409, a dropped connection).
        // Treated as the same UNKNOWN as `check_failed`: no shake, no
        // celebration, row still undecided. The one thing it must not become is
        // a silent no-op on a control that looks like a radio.
        clearRecheckPending(contactId);
        setRecheckOutcomes((prev) => ({
          ...prev,
          [contactId]: {
            outcome: "check_failed",
            detail: null,
            attempt: ++recheckAttemptRef.current,
          },
        }));
        toast.message(RECHECK_CHECK_FAILED);
      }
    );
  };

  /**
   * What a row shows about its own add — the single place that decides.
   *
   * FOUR STATES, and only ONE of them retires the row. The synchronous add
   * exists so this function can tell them apart honestly:
   *
   *  · in flight → "Adding to the search…", row STAYS
   *  · added     → the celebration, then the row retires
   *  · pending   → the job outrode our patience. NOT an error: the row stays
   *                and says the write is still going.
   *  · failed    → the reason, row stays so it can be retried
   *
   * Returns `undefined` when nothing has been attempted, which is what the
   * shell renders as "no slot at all".
   */
  const addStatusNode = (contactId: string): ReactNode | undefined => {
    if (addingIds.has(contactId)) {
      return (
        <output
          data-testid={`add-progress-${contactId}`}
          className="flex items-center gap-2 rounded-md border border-border bg-surface px-2.5 py-1.5 text-label text-text-secondary"
        >
          <Spinner size="sm" />
          {ADDING_TO_SEARCH_LINE}
        </output>
      );
    }
    const result = addOutcomes[contactId];
    if (!result) return undefined;
    if (result.outcome === "added") {
      // ONE component for both, because the RETIREMENT is the behaviour and the
      // paper is the decoration. The flag decides `burst`, never whether the row
      // leaves — a recruiter without gamification still gets a tidy list.
      return (
        <CleanupCelebration
          testId={
            celebrationsEnabled && result.celebrate
              ? `added-celebration-${contactId}`
              : `add-done-${contactId}`
          }
          // The burst says WELL DONE; the list says what was done. Repeating
          // the detail inside a celebration that lasts a second and a half
          // reads as a notification, not as a reward.
          line={
            celebrationsEnabled && result.celebrate
              ? WELL_DONE_LINE
              : result.updated
                ? UPDATED_IN_THRIVE_LINE
                : ADDED_TO_SEARCH_LINE
          }
          // The flag REVEALS the burst; the decision kind EARNS it.
          burst={celebrationsEnabled && Boolean(result.celebrate)}
          magnitude={result.magnitude ?? 1}
        />
      );
    }
    if (result.outcome === "skipped") {
      // ITS OWN ARM. The worker LOOKED and declined — that is a verdict, and
      // routing it through the failure line told the recruiter something broke
      // when nothing had. Neutral tone, and the worker's own reason when it
      // sent one, because "why" is the only actionable part.
      return (
        <output
          data-testid={`add-skipped-${contactId}`}
          className="rounded-md border border-border bg-surface px-2.5 py-1.5 text-label text-text-secondary"
        >
          {ADD_SKIPPED_LINE}
          {result.detail ? ` ${result.detail}` : ""}
        </output>
      );
    }
    if (result.outcome === "pending") {
      return (
        <output
          data-testid={`add-pending-${contactId}`}
          className="rounded-md border border-border bg-surface px-2.5 py-1.5 text-label text-text-secondary"
        >
          {ADD_STILL_RUNNING_LINE}
        </output>
      );
    }
    return (
      <output
        data-testid={`add-failed-${contactId}`}
        className="rounded-md border border-warning-strong bg-warning-muted px-2.5 py-1.5 text-label text-warning-ink"
      >
        {ADD_FAILED_LINE}
        {result.detail ? ` ${result.detail}` : ""}
      </output>
    );
  };

  /**
   * `Confirm all` on Update-for-current-role — it WRITES now (operator,
   * 2026-09-11), instead of only ticking boxes for a later batch.
   *
   * Every ticked row is a contact Thrive already holds; confirming one says
   * "give it the new job". So each goes through the same single-contact add the
   * decision cards use, with `update_anyway` — nothing about "all of them"
   * changes what one of them means.
   *
   * SEQUENTIAL, not fanned out. One add is one Thrive write plus a poll, and
   * firing thirty at once would put thirty jobs on a queue whose worker handles
   * them one at a time anyway — the recruiter would wait exactly as long and
   * the API would hold thirty open requests to do it. Rows disappear as they
   * land, which is the progress indicator.
   *
   * `matchedBefore` is TRUE by construction here: a company change IS a matched
   * contact. That is what makes the line read "Updated in Thrive".
   */
  /** True while `Confirm all` is working through the ticked rows. It writes one
   *  contact at a time, so the pass takes as long as the rows it covers — the
   *  button has to say so rather than look idle. */
  const [confirmingAll, setConfirmingAll] = useState(false);

  const confirmAllCompanyChanges = async () => {
    const ids = tickedCompanyChanges.map((c) => c.import_contact_id);
    // IT ACTS ON THE TICKED ROWS ONLY (operator, 2026-09-11). The first cut
    // ticked everything before writing, which quietly converted "confirm the
    // ones I chose" into "confirm all of them" — the untick is the recruiter
    // saying *not this one*, and a bulk control must not overrule it. Rows left
    // unticked stay exactly where they are, still pending.
    if (!api?.addOneContactToSearch || !importId) return;
    setConfirmingAll(true);
    setConfirmAllNeedsCompareCount(0);
    let needsCompare = 0;
    try {
      for (const id of ids) {
        // `addOneAndReport` retires it on success; a row that did NOT get written
        // stays, carrying its own reason.
        const c = companyChanges.find((x) => x.import_contact_id === id);
        // ONE AT A TIME, and the server enforces it: a second job while the
        // first is in flight is refused with a 409. The write itself already
        // waits for its own outcome, so the loop is sequential — but the run
        // claim is released a beat AFTER the row turns terminal, so back-to-back
        // calls can still collide. A short breath costs nothing on a pass that
        // is seconds long anyway.
        await new Promise((r) => setTimeout(r, 400));
        const outcome = await addOneAndReport(id, {
          w2Decision: "update_anyway",
          matchedBefore: true,
          // The company is what a job move IS, and the recruiter just read it
          // on the row — naming it makes the line checkable rather than
          // generic.
          doneLine: doneChangedRole(contactName(c), c?.company ?? null),
        });
        // A row left out for want of a recent Compare does not stop the
        // pass: rows compared later still go (sub-task 04). Counted, and said
        // once at the end.
        if (outcome === "needs_compare") needsCompare += 1;
      }
    } finally {
      setConfirmAllNeedsCompareCount(needsCompare);
      setConfirmingAll(false);
    }
  };

  /** The name on the card, which is what the recruiter remembers doing the
   *  work to. Falls back to a phrase rather than to an id: a done-list entry
   *  naming a UUID tells the person nothing about what they achieved. */
  const contactName = (c?: { first_name?: string | null; last_name?: string | null }): string =>
    [c?.first_name, c?.last_name].filter(Boolean).join(" ").trim() || "This contact";
  const nameOf = (contactId: string): string =>
    contactName(
      differentPeopleAll.find((d) => d.import_contact_id === contactId) ??
        w2Contacts.find((c) => c.import_contact_id === contactId) ??
        (result?.conflicts?.w1_duplicate_linkedin ?? [])
          .flatMap((g) => g.contacts ?? [])
          .find((c) => c.import_contact_id === contactId)
    );

  /**
   * How many duplicate Thrive records a card was holding before the recruiter
   * cleaned it — the size of the job they just did.
   *
   * Taken from the compare result, which is the snapshot of the mess as it was;
   * the re-check reports only what SURVIVED, so it cannot measure the work. A
   * W1 group carries one member per duplicate, and a different-people row
   * carries its excluded pairs.
   */
  const cleanedCountFor = (contactId: string): number => {
    const w1 = (result?.conflicts?.w1_duplicate_linkedin ?? []).find((g) =>
      (g.contacts ?? []).some((c) => c.import_contact_id === contactId)
    );
    if (w1) return Math.max((w1.contacts ?? []).length - 1, 1);
    const dp = differentPeopleAll.find((x) => x.import_contact_id === contactId);
    if (dp) return Math.max((dp.excluded ?? []).length, 1);
    return 1;
  };

  const conflictActionsEnabled = Boolean(api && importId);

  // Retired rows leave EVERY pending list, not just the different-people one.
  //
  // Until 2026-09-11 `celebratedIds` filtered that list alone, so a W1 whose
  // merge had just been verified — and a conflict card whose update had just
  // been written — stayed on screen with their celebration under them. Combined
  // with a celebration that could replay, that is the "papeles hasta el
  // infinito" the operator saw: the row never left, so the burst never stopped
  // having something to mount on.
  const retired = (contactId: string | null | undefined): boolean =>
    contactId != null && celebratedIds.has(contactId);

  /**
   * IS THIS ROW STILL PENDING WORK? — the one question every conflict list has
   * to ask, and until now only one of them did.
   *
   * `retired` remembers what THIS SITTING resolved and dies with the modal.
   * That was the whole of it, so closing the modal resurrected everything: a
   * contact already written to Thrive was offered a decision again, answering
   * it wrote nothing (the export is idempotent), and the card stayed — "no
   * desaparece", four times over (operator, 2026-09-12).
   *
   * `review_status` is the SERVER's answer to the same question and survives
   * anything the client forgets. `exported` means it is in Thrive; `ignored`
   * means the recruiter dismissed it and it lives in the Skipped tab. Neither
   * belongs in a queue of work to do.
   */
  const settled = (c: {
    import_contact_id: string;
    review_status?: string | null;
  }): boolean =>
    retired(c.import_contact_id) || c.review_status === "exported" || c.review_status === "ignored";

  /**
   * The numbers behind the running bar, or `null` for "draw no bar".
   *
   * Three ways to get `null`, and each is a case where a determinate bar would
   * assert something untrue:
   *
   *   * NO `progress` ON THE WIRE. Either a run started by a worker from before
   *     progress was reported, or one whose api is older than its worker. A bar
   *     would have to invent a denominator.
   *   * NO `result` YET. Between the POST and the first poll answer there is
   *     nothing at all — this is the first second of every run.
   *   * `total === 0`. `ProgressBar` renders `max <= 0` as an empty track that
   *     can never fill, and "0 of 0" is not a progress statement.
   *
   * All three fall through to the indeterminate bar — motion with no
   * percentage, the honest "alive, amount unknown".
   */
  const runProgress = useMemo(() => {
    const p = result?.progress;
    if (!p || p.total <= 0) return null;
    return {
      processed: p.processed,
      total: p.total,
      label: compareProgressPhaseLabel(p.phase),
    };
  }, [result?.progress]);

  const w1Groups = (result?.conflicts?.w1_duplicate_linkedin ?? []).filter((g) => {
    const first = g.contacts?.[0];
    return first ? !settled(first) : true;
  });
  // W2 clusters are 1 contact each (detect_w2's design) — flatten to a
  // single paginated list of cards. Cards resolved as not-the-same-person are
  // hidden for THIS view only (see `dismissedInView`).
  // One fetch per open, aborted on close. `review_status=ignored` is the
  // server's own partition member — nothing here re-derives "is it ignored".
  useEffect(() => {
    // `api` is an OPTIONAL prop and several call sites pass a partial client —
    // isolated tests stub only the methods they exercise. Feature-detecting the
    // method, not just the object, keeps a surface that never had an archive
    // from crashing on one, and matches how the other calls here are guarded.
    if (!api?.listContacts || !importId) return;
    const controller = new AbortController();
    void api
      .listContacts(
        importId,
        // 100 is the endpoint's MAXIMUM (`Query(ge=1, le=100)`), not a
        // preference. The first cut sent 200 and every request came back 422 —
        // silently, because of the handler below. Past 100 ignored contacts the
        // section under-reports, which is a real limit and a better failure
        // than the 422 was: it is visible in the count.
        //
        // It is therefore the endpoint's CAP — the first 100, in one request,
        // with the under-report past that named above — not a page size:
        // deliberately NOT `listPageSize()` from `@nu-assist/shared-fe`, which
        // resolves per stage (20 local / 50 dev / 100 prod) for the three
        // visible lists. Folding this call site into it would cap the ignored
        // section at 20 rows on local while the code still reads as a bulk pull.
        { page: 1, page_size: 100, review_status: "ignored" },
        { signal: controller.signal }
      )
      .then(
        (page) => {
          setPriorIgnored(page.items ?? []);
          setPriorIgnoredTotal(page.total ?? (page.items ?? []).length);
        },
        (error) => {
          // Quiet for the RECRUITER, loud for the developer. The modal must
          // still open — everything they are about to act on is in the other
          // tab — but a bare empty handler is how this shipped broken: the
          // request 422'd on every open (`page_size=200` over a `le=100` cap)
          // and the tab read `Ignored (0)`, which is indistinguishable from
          // having ignored nobody. That is the exact silence this whole epic
          // exists to remove, reintroduced in the fix for it.
          //
          // An abort is not a failure — it is this effect's own cleanup on
          // close, and logging it would cry wolf on every modal dismissal.
          if ((error as { name?: string } | null)?.name !== "AbortError") {
            console.error("compare-modal: could not load the ignored archive", error);
          }
        }
      );
    return () => controller.abort();
  }, [api, importId]);

  const w2Contacts = (result?.conflicts?.w2_identity_mismatch ?? [])
    .flatMap((group) => group.contacts ?? [])
    .filter((c) => !dismissedInView.has(c.import_contact_id));
  // W3 clusters are 1 contact each (detect_w3's design), same as W2 — flatten
  // to a single paginated list of cards.
  const w3Contacts = (result?.conflicts?.w3_unverified_name_match ?? [])
    .flatMap((group) => group.contacts ?? [])
    .filter((c) => !dismissedInView.has(c.import_contact_id));
  // Tab partition (sub-task 11): ignored (left-out) cards live in the Ignored
  // tab, so the Needs review tab holds only actionable cards. The optimistic
  // overrides in `isLeftOut` make a Leave-out (or its Undo) move the card
  // between the tabs immediately, before the next poll confirms it.
  const w2Active = w2Contacts.filter((c) => !isLeftOut(c));
  const w3Active = w3Contacts.filter((c) => !isLeftOut(c));
  // The Ignored tab keeps the PENDING tab's grouping, by the same rule that
  // built it (`names_match`, server-derived). It used to be one flat list, so a
  // row you ignored from `Company change` came back indistinguishable from one
  // you ignored from `Needs your decision` — and worse, it came back as a
  // different SHAPE, since every ignored contact rendered as a full
  // `W2ConflictCard`. A one-line row became a four-action card the moment you
  // dismissed it, which is the opposite of what an archive should do.
  //
  // W3 never splits: it matched BY name, so its names always agree and routing
  // on that alone would file every W3 under `Company change`. Same carve-out
  // the pending tab makes, for the same reason.
  // Derived from the SAME two source lists the pending tab splits, not from a
  // re-test of the contact — `companyChanges` is `w2Active.filter(names_match)`
  // and W3 is excluded by construction because it is not in `w2Contacts` at
  // all. Mirroring the source lists is what keeps the two tabs' grouping from
  // drifting apart, rather than two independent predicates that agree today.
  const ignoredCompanyChanges = w2Contacts.filter((c) => isLeftOut(c) && c.names_match);
  const ignoredDecisions = [
    ...w2Contacts.filter((c) => isLeftOut(c) && !c.names_match),
    ...w3Contacts.filter((c) => isLeftOut(c)),
  ];
  // The Different people list. Server-derived live from the registry (a marked
  // contact classifies NEW and is therefore absent from `conflicts` entirely),
  // minus the pairs this view has just retired. A card with every pair undone
  // drops out; one with pairs left keeps the rest, because those are separate
  // decisions that separate people may have made.
  //
  // DERIVED HERE, ABOVE THE ARCHIVE COUNTS, because a different-people row can
  // now be SKIPPED (fu5 sub-task 02, Part B) and a skipped one belongs to the
  // Skipped tab's total. Below them it would have been counted nowhere.
  const differentPeopleAll = (result?.different_people ?? [])
    .map((decision) => ({
      ...decision,
      excluded: (decision.excluded ?? []).filter(
        (ref) => !undoneInView.has(`${decision.import_contact_id}:${ref.thrive_id}`)
      ),
    }))
    .filter((decision) => decision.excluded.length > 0);
  // It partitions like every other card kind: pending on one side, archive on
  // the other. The alternative — dropping a skipped row from the pending
  // section and filing it nowhere — is the silent disappearance this modal
  // keeps removing. The Skip on this card is the SAME persisted `leave-out` the
  // conflict cards use, so it lands in the same tab, and the same
  // optimistic-then-server rule decides which side it is on (`isLeftOut`'s
  // rule, applied to an id because these are not `ConflictContact`s).
  const isDifferentPersonLeftOut = (contactId: string): boolean =>
    leftOutOverrides[contactId] ?? serverLeftOut.has(contactId);
  const differentPeople = differentPeopleAll.filter(
    (decision) =>
      // A ROW ALREADY IN THRIVE IS NOT PENDING WORK. The registry pair behind
      // this card is firm-wide and permanent, and the in-session retirement
      // dies with the modal — so without this the card came back on every open
      // and nothing the recruiter did could retire it. "Aparece luego de
      // cerrada la modal… no hay forma de sacarlo" (operator, 2026-09-11).
      decision.export_status !== "exported" &&
      !isDifferentPersonLeftOut(decision.import_contact_id) &&
      // Retired by the celebration, once its burst has finished playing
      // (operator, 2026-09-10). VIEW-ONLY, and the distinction matters: the row
      // is gone from this list because the recruiter watched it resolve, NOT
      // because anything was written. A reopened modal re-derives the collision
      // from the surviving registry pair and the card comes back — the same
      // honesty the cleared verdict line already states out loud.
      !retired(decision.import_contact_id)
  );
  const skippedDifferentPeople = differentPeopleAll.filter((decision) =>
    isDifferentPersonLeftOut(decision.import_contact_id)
  );
  // Dismissed in an EARLIER session: present in the fetch, absent from this
  // run's conflicts. Minus anything the grouped lists already show (a row
  // ignored moments ago is in both), and minus anything undone in this session
  // — the fetch predates the Undo and still believes the row is ignored.
  //
  // The skipped different-people rows are in here too, for BOTH of this set's
  // jobs: they must not be double-counted by `priorOnly` when an earlier
  // session skipped them (the `listContacts` fetch would carry them), and they
  // are part of `ignoredTotal`.
  const groupedIds = new Set([
    ...[...ignoredCompanyChanges, ...ignoredDecisions].map((c) => c.import_contact_id),
    ...skippedDifferentPeople.map((d) => d.import_contact_id),
  ]);
  // `leftOutOverrides` is the SAME optimistic map `isLeftOut` consults, so an
  // Undo removes the row here on the same render it removes it from the cards.
  // Reading the map directly rather than through `isLeftOut` only because that
  // helper takes a `ConflictContact` and these are `ImportContactRow`s — the
  // rule is identical.
  const priorOnly = priorIgnored.filter(
    (r) => !groupedIds.has(r.import_contact_id) && leftOutOverrides[r.import_contact_id] !== false
  );
  // The server's total for the fetched half, MINUS anything already counted in
  // the grouped sections or undone in this session — both of which are inside
  // the fetched page when they are in it at all.
  const priorNotShown = Math.max(0, priorIgnoredTotal - priorIgnored.length);
  const ignoredTotal = groupedIds.size + priorOnly.length + priorNotShown;
  // THEY GO IN `Needs your decision`, not in a section of their own.
  //
  // A section titled "Ignored earlier" published a distinction the RECRUITER
  // does not have — they dismissed the contact, and whether the dismissal
  // predates this compare run is a fact about our data retention, not about
  // their work. It was showing them my limitation dressed as information
  // (operator, 2026-09-08: "todo esto parece estar de más").
  //
  // `Needs your decision` is the conservative home, and the choice is not
  // arbitrary. `Company change` ASSERTS "same person, new employer", which is
  // exactly the claim `names_match` would have licensed and which these rows
  // cannot support — filing them there would put a sentence on screen that
  // nothing backs. The other section asserts only that the identity question
  // was open, which is true of every conflict that ever raised one.

  // Banner input: every visible conflict contact carrying the firm-wide
  // duplicate-LinkedIn warning (`ConflictContact.duplicate_linkedin`, the same
  // live derivation the contacts table's cell marker reads).
  const dupContacts = [
    ...w1Groups.flatMap((group) => group.contacts ?? []),
    ...w2Contacts,
    ...w3Contacts,
  ].filter((c) => c.duplicate_linkedin);
  const w1Count = result?.w1_count ?? 0;
  const w2Count = result?.w2_count ?? 0;
  const w3Count = result?.w3_count ?? 0;
  // `different_people` COUNTS, and leaving it out was a deadlock.
  //
  // This gates the whole tabbed body, so a run with `w1/w2/w3_count === 0` and
  // one same-URL card rendered the green "No conflicts — every contact can be
  // added automatically" line with that card NOWHERE on screen. `pendingTotal`
  // counts it, so `verifiedClear` stayed false and the footer offered another
  // look forever: work the recruiter could neither see nor finish.
  //
  // Reachable straight off this PR's own path — mark "not the same person" on
  // the last W2 and re-run (/pr-review iter 2, Agent C, C1). Benign on `dev`,
  // where the export CTA rendered unconditionally; this branch is what turned
  // it into a dead end.
  const hasConflicts = w1Count > 0 || w2Count > 0 || w3Count > 0 || differentPeople.length > 0;
  // Every conflict card left THIS VIEW (marked not-the-same-person) while
  // conflicts still exist. The success line "No conflicts — every contact can
  // be added automatically" would be FALSE in this state (the run's conflicts
  // are only resolved on the NEXT compare), so a dedicated honest line
  // renders instead. Only 'Not the same person' removes cards — every other
  // answer keeps them visible — so this state needs every card acted on.
  const allConflictsHandledInView =
    hasConflicts &&
    dismissedInView.size > 0 &&
    w1Groups.length === 0 &&
    w2Contacts.length === 0 &&
    w3Contacts.length === 0;
  // THE FOUR PENDING SECTIONS, ordered by how cheaply each closes. Same ranking
  // principle the previous revision applied to one merged queue; sections make
  // it readable rather than inferable, and — because each keeps its own header
  // and count — no amount of work is ever silently out of view.
  //
  //   1. Company change      — same name, new employer. Confirm and move on.
  //   2. Needs your decision — the real identity conflicts, plus every W3.
  //   3. Different people    — decided, but a URL still has to be fixed.
  //   4. Can't be resolved   — W1. Go to Thrive.
  //
  // THE SPLIT AT (1)/(2) IS THE POINT. `_identity_mismatch` keys on
  // name + company, so an employer move breaks it exactly as a different person
  // on the same URL does — and the move is the normal case in a recruiting
  // database. Measured on the shipped fixture: every W2 row is a company
  // change, so the old single block showed a wall of "conflicts" containing no
  // conflict at all, which is what the operator hit.
  //
  // `names_match` is SERVER-derived (same normalizer as the rule it splits), so
  // nothing here re-derives "same person". W3 is excluded from the split by
  // construction: it matched BY name, so its names always agree and routing on
  // that alone would file every W3 as a company change.
  const companyChanges = w2Active.filter((c) => c.names_match).filter((c) => !settled(c));
  /** The rows `Confirm all` will actually write — the ONE set both the button's
   *  label and its handler read, so they cannot disagree again. */
  const tickedCompanyChanges = companyChanges.filter(
    (c) => !unconfirmedCompanyChanges.has(c.import_contact_id)
  );
  const identityConflicts = w2Active.filter((c) => !c.names_match);
  const needsDecisionItems: ConflictContact[] = [...identityConflicts, ...w3Active].filter(
    (c) => !settled(c)
  );
  // EVERY CONFLICT ROW ENDS THE SESSION DECIDED, and this is what enforces it.
  //
  // Only `Needs your decision` rows GATE it. `Company change` rows are
  // pre-ticked (a deliberate exception with a reason on the record — see
  // `COMPANY_CHANGE_LEAD`) and the different-people rows are already decided.
  //
  // W1 is excluded, and NOT because it "offers nothing" — it grew two options
  // on 2026-09-10 and this comment went on claiming otherwise for a whole
  // epic (/pr-review iter 1, agent C: the comment contradicted
  // `W1ConflictCard.tsx` inside the same diff). The real reason is that the
  // fix is not available here: a W1 asks the recruiter to go and merge in
  // Thrive, which they may legitimately not do today, and `resolve_export_plan`
  // skips a `duplicate` row whatever this modal was told. Blocking Continue on
  // an answer that changes no outcome would be the "never hard-block the
  // export" rule broken by the one card kind that cannot honour it.
  //
  // A row is counted only if it actually RENDERS options. `conflictActionValues`
  // is the card's own derivation, imported rather than restated: a set can be
  // empty on a surface with no api client, and a row that can never be answered
  // would otherwise disable Continue forever. Reading the card's function is
  // also what keeps the gate honest if the derivation ever changes — a twin
  // would agree today and drift on the first edit.
  const undecidedIds = needsDecisionItems
    .filter(
      (c) =>
        actionFor(c.import_contact_id) === null &&
        conflictActionValues(c, w3Active.includes(c) ? "w3" : "w2", conflictActionsEnabled).length >
          0
    )
    .map((c) => c.import_contact_id);
  // NO BULK ESCAPE HATCH. A "Skip all remaining" was proposed during design and
  // rejected by the operator: clearing 50 rows in one click is the neutral
  // default coming back through the door under a different label, and it
  // removes the "somebody looked at each row" property more efficiently than
  // the default it would replace. Settled, not open — there is deliberately no
  // control anywhere in this modal that decides more than one conflict row.
  const undecidedCount = undecidedIds.length;
  // Everything still asking for something — the header count. Different people
  // IS pending: marking "not the same person" answers one question and opens
  // another, and nothing else in the product tracks the URL collision it leaves
  // behind.
  const pendingTotal =
    companyChanges.length + needsDecisionItems.length + differentPeople.length + w1Groups.length;
  // WHAT THE RUN VERIFIED, recorded when it lands and not recomputed after.
  //
  // Snapshotting is the point. `pendingTotal` keeps moving as the recruiter
  // works — every Skip settles a row and takes it out — and a CTA following it
  // live would turn "I dismissed the last card" into "Thrive is clean", which
  // is a different claim and the one this button makes. Pressing the re-run is
  // what asks the question again.
  //
  // Reads `pendingTotal` rather than the run document's own `w1_count` /
  // `w2_count`: those count conflicts the recruiter has already ignored, so a
  // sitting that legitimately skipped every row would never see the export
  // again. `pendingTotal` is the same number the header shows, minus exactly
  // what `settled()` calls finished (exported / ignored / retired).
  useEffect(() => {
    const jobId = (result as { job_id?: string } | null)?.job_id;
    if (!jobId || isRunning || jobId === verifiedJobRef.current) return;
    // WAIT FOR THE RECONCILIATION, and this is not an ordering nicety.
    //
    // `pendingTotal` is computed during RENDER from `settled()`, which reads
    // `retired()` -> `celebratedIds`. The reconciliation effect above clears
    // that overlay, but it runs in the same commit and cannot change a number
    // the render already produced. So the first render carrying a new job
    // offers a total still deflated by cards the VIEW retired — and since the
    // line below pins `verifiedJobRef`, the corrected total on the next render
    // was never looked at.
    //
    // A card retired by a celebration, plus a re-run that returns the same
    // collision (the registry pair survives, so the server still reports it),
    // left `verifiedClear` true for good: the card came back on screen while
    // the footer swapped to the export (/pr-review iter 1, Agent C).
    //
    // `refreshFrom` is non-null for exactly the window between pressing the
    // re-run and the reconciliation finishing, so this reads as "do not judge a
    // run whose overlays have not been cleared yet". It is null on a first
    // compare, which needs no clearing and is judged immediately.
    if (refreshFrom !== null) return;
    verifiedJobRef.current = jobId;
    setVerifiedClear(pendingTotal === 0);
  }, [result, isRunning, pendingTotal, refreshFrom]);

  // Clamp to the CURRENT list's page count on every render — derived, not
  // synced via an effect — so a retry (or any poll) that returns a smaller
  // conflict set than the page the recruiter was on self-heals immediately
  // instead of rendering an out-of-range "Showing X-Y of Z" with an empty item
  // list (/pr-review iter 1 finding).
  const paginate = <T,>(items: T[], requested: number): { items: T[]; clamped: number } => {
    const clamped = Math.min(
      requested,
      Math.max(1, Math.ceil(items.length / CONFLICTS_PAGE_SIZE)) - 1
    );
    return {
      items: items.slice(clamped * CONFLICTS_PAGE_SIZE, (clamped + 1) * CONFLICTS_PAGE_SIZE),
      clamped,
    };
  };
  const companyChangePaged = paginate(companyChanges, companyChangePage);
  const needsDecisionPaged = paginate(needsDecisionItems, needsDecisionPage);
  const cantResolvePaged = paginate(w1Groups, cantResolvePage);
  // Which sections the chips are showing. `all` shows every one.
  const showSection = (section: PendingSection): boolean =>
    pendingFilter === "all" || pendingFilter === section;
  // WHAT CONTINUE ACTUALLY SENDS. The company-change section renders its rows
  // pre-ticked, and a ticked box the recruiter never touched is a promise —
  // the only way to keep it is to MATERIALIZE the default into a real
  // decision. `w2Decisions` alone holds nothing for an untouched row, and the
  // server defaults an unlisted contact to `skip`, so sending it raw would show
  // "we'll update the company in Thrive" and then update nothing.
  //
  // Explicit state wins over the default, which is what makes unticking work:
  // untick puts the id in `unconfirmedCompanyChanges` and this leaves it out,
  // and any of the four card actions writes `w2Decisions` directly.
  const effectiveDecisions = useMemo(() => {
    const out: Record<string, W2Decision> = {};
    for (const c of companyChanges) {
      if (!unconfirmedCompanyChanges.has(c.import_contact_id)) {
        out[c.import_contact_id] = "update_anyway";
      }
    }
    return { ...out, ...w2Decisions };
  }, [companyChanges, unconfirmedCompanyChanges, w2Decisions]);
  const differentPeoplePaged = paginate(differentPeople, differentPeoplePage);
  const skippedDifferentPeoplePaged = paginate(skippedDifferentPeople, skippedDifferentPeoplePage);
  const ignoredCompanyChangePaged = paginate(ignoredCompanyChanges, ignoredCompanyChangePage);
  // ONE LIST, ONE PAGER. The two halves used to be paged separately off the
  // SAME index while the section's only pager was handed `ignoredDecisions`'
  // total — the other list. In the common case that total is 0 (Compare
  // excludes previously-dismissed rows from scope, so `ignoredDecisions` is
  // empty exactly when `priorOnly` is not), `ConflictPager` returned null and
  // 10 of up to 100 rows rendered with no control, under a header stating the
  // full count. The same class of defect as the `Ignored (0)` this round
  // fixed, one threshold up. Reported by iter-4 review (A-N1, C-C2).
  //
  // Discriminated rather than coerced: the two carry different wire shapes
  // (`ConflictContact` has the conflict facts, `ImportContactRow` does not), and
  // flattening them would be the cast that was already rejected once here.
  const ignoredDecisionItems: (
    | { kind: "card"; contact: ConflictContact }
    | { kind: "row"; row: ImportContactRow }
  )[] = [
    ...ignoredDecisions.map((contact) => ({ kind: "card" as const, contact })),
    ...priorOnly.map((row) => ({ kind: "row" as const, row })),
  ];
  const ignoredDecisionPaged = paginate(ignoredDecisionItems, ignoredDecisionPage);
  // Presign the durable stored-avatar keys of ONLY the visible pages (each card
  // renders the durable S3 copy; no stored key -> initials, the web has no licdn
  // fallback). Scoped to the pages — NOT the whole lists — so the batch body
  // stays small: a large-conflict import would otherwise exceed the ALB WAF ~8KB
  // body cap and 403 (the exact failure class this epic fixes). Page turns
  // re-resolve the next <=CONFLICTS_PAGE_SIZE keys.
  //
  // SEVEN sections contribute a page each — the pending tab's four, plus the
  // Skipped tab's three (two since it gained the pending tab's grouping, and a
  // third since a different-people row can be skipped) — so the worst case is
  // 7 * CONFLICTS_PAGE_SIZE keys rather than 5. Still well inside the cap, and
  // still bounded by the page size rather than by the import, which is the
  // property that matters: the bound does not grow with the number of
  // conflicts, only with the number of sections.
  //
  // A W1 group contributes ONE key, not one per member: its members are the
  // same import row repeated per Thrive duplicate and carry the same key.
  const resolvedAvatars = useResolveAvatars(api, [
    ...companyChangePaged.items,
    ...needsDecisionPaged.items,
    ...cantResolvePaged.items.map((group) => group.contacts?.[0]).filter((c) => c != null),
    ...differentPeoplePaged.items,
    ...skippedDifferentPeoplePaged.items,
    ...ignoredCompanyChangePaged.items,
    ...ignoredDecisionPaged.items.map((i) => (i.kind === "card" ? i.contact : i.row)),
  ]);

  // The run drops TWO sets — exported and dismissed — so the number that
  // promises what it covers must account for both. Subtracting only the
  // exported ones put this at odds with `PreCompareConfirmation`, which has
  // always accounted for both: the confirmation would say "Compare all 4" and
  // the modal it opened "(6 pending)".
  //
  // `pendingOverride` is the page's exact answer when it has one. The fallback
  // is the old exported-only arithmetic — over-stating, but never inventing.
  const pendingCount = pendingOverride ?? Math.max(0, scopeTotal - alreadyExportedCount);
  // The "(X pending)" suffix appears whenever the run covers LESS than the
  // scope — which is not the same as "something is exported". An import with
  // nothing exported but some rows dismissed narrows too, and gating on
  // `alreadyExportedCount > 0` made the suffix vanish exactly there, leaving
  // the confirmation saying "Compare all 7" and the modal saying nothing.
  const scopeNarrowed = scopeTotal > 0 && pendingCount < scopeTotal;

  /** One row for a contact dismissed BEFORE this compare run.
   *
   *  Rendered thinner than the cards beside it, on purpose: Compare excludes
   *  dismissed rows from scope, so it never examined these and the conflict
   *  facts (`names_match`, the Thrive-side company) do not exist to show. The
   *  degradation is per ROW rather than a section header announcing our data
   *  model to a recruiter who only ever "ignored a contact".
   *
   *  A function rather than inline JSX because the two shapes now share ONE
   *  paginated list — see `ignoredDecisionItems`. */
  const renderPriorIgnoredRow = (r: ImportContactRow) => (
    <li
      key={r.import_contact_id}
      data-testid="ignored-prior-row"
      className="flex items-center gap-3 rounded-md border border-border bg-surface px-3 py-2 opacity-60"
    >
      {/* The IMPORT side's face, from the durable
                                            S3 copy like everywhere else — never the
                                            licdn `profile_image_url`, which expires
                                            in ~2 weeks and would render broken on an
                                            old import. */}
      <Avatar
        src={r.profile_image_s3_key ? resolvedAvatars.get(r.profile_image_s3_key) : undefined}
        name={[r.first_name, r.last_name].filter(Boolean).join(" ") || undefined}
        // Same `lg` as the rows and cards beside
        // it: recovered or dismissed just now,
        // an ignored contact looks the same.
        size="lg"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-body text-text">
          {[r.first_name, r.last_name].filter(Boolean).join(" ") || "This row"}
        </p>
        <p className="truncate text-caption text-text-secondary">
          {/* `current_company`, the employer the
                                                IMPORT's own CSV carries — not
                                                `primary_company`, which the parser
                                                leaves empty and which rendered this
                                                line as "no employer on file" on a
                                                row that has one (operator,
                                                2026-09-08: "el contacto ignore
                                                pierde las companies"). The contacts
                                                table reads the same field, so the
                                                two now agree about one contact.

                                                The Thrive side is genuinely absent
                                                — it is not persisted — so this is
                                                the import's half of what used to be
                                                an arrow, and it is stated as a
                                                plain fact rather than as half a
                                                transition. */}
          {[r.current_company, r.current_position]
            .map((part) => part?.trim())
            .filter(Boolean)
            .join(" · ") || "no employer on file"}
        </p>
        <p className="truncate text-caption text-text-muted">
          {r.left_out_at ? formatIgnoredDate(new Date(r.left_out_at), new Date()) : LEFT_OUT_PILL}
        </p>
      </div>
      {/* THRIVE's face beside it, `name` withheld
                                            on purpose: initials drawn from the
                                            import row's name printed under Thrive's
                                            id would assert the two are the same
                                            person. Same rule the cards follow. */}
      <div className="flex shrink-0 items-center gap-2">
        <Avatar src={r.thrive_avatar_url} size="lg" />
      </div>
      {r.contact_id_thrive && (
        <a
          href={thriveContactUrl(r.contact_id_thrive)}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 text-caption text-accent hover:underline"
        >
          #{r.contact_id_thrive} →
        </a>
      )}
      <button
        type="button"
        data-testid={`ignored-prior-undo-${r.import_contact_id}`}
        onClick={() => undoLeaveOut(r.import_contact_id)}
        className="shrink-0 rounded-md border border-border bg-bg px-2 py-1 text-label text-text hover:bg-surface"
      >
        {UNDO_LEAVE_OUT_LABEL}
      </button>
    </li>
  );

  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: native <dialog> needs
      // imperative showModal()/close() which doesn't fit this conditionally-
      // rendered React overlay; role="dialog" + aria-modal + aria-labelledby is
      // the standard accessible pattern here.
      role="dialog"
      aria-modal="true"
      aria-labelledby="compare-thrive-title"
      data-testid="compare-thrive-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-lg">
        {/* Header — the plain `border-b` is replaced by the progress divider,
            which shows the already-exported fraction of the current scope (and
            renders exactly as the old plain hairline when nothing is exported). */}
        <div className="flex flex-col">
          <div className="flex items-start justify-between gap-3 p-5">
            <div>
              <h2 id="compare-thrive-title" className="text-h1 text-text">
                Compare to Thrive
              </h2>
              <p className="text-caption text-text-secondary mt-1">
                Dry run — a preview of what {actionLabel} will do. Nothing is written yet.
              </p>
            </div>
          </div>
          <CompareProgressDivider
            alreadyExported={alreadyExportedCount}
            total={scopeTotal}
            scope={scope}
          />
        </div>

        {/* Body (scrolls) */}
        <div className="flex-1 overflow-y-auto p-5">
          {nothingToCompare ? (
            // A manual selection (or, via a live poll ticking to 100% while the
            // whole-import modal is open, the whole import) that resolved to
            // zero pending contacts: no dry-run was started (autoStart=false).
            // Only Close is offered — there is nothing pending to act on.
            <div
              data-testid="compare-nothing"
              className="mt-5 rounded-md border-l-4 border-success bg-surface p-3 text-body text-success"
            >
              {scope === "selection"
                ? "Nothing to compare. Every contact you selected is already added."
                : "Nothing to compare. Every contact in this people list is already added."}
            </div>
          ) : isRunning ? (
            // The wrapper and its testid are LOAD-BEARING beyond this file:
            // `tests/e2e/helpers/pre-compare.ts` waits on `compare-running` to
            // detect the transient state. The bar goes inside it rather than
            // beside it, so that helper keeps working whichever branch renders.
            <div
              data-testid="compare-running"
              className="mt-5 flex flex-col gap-2 text-body text-text-secondary"
            >
              {runProgress ? (
                // `compare-progress-BAR`, not `compare-progress`, so the id
                // names one element rather than reading as a parent of the
                // unrelated `compare-progress-divider` in the header.
                //
                // It does NOT remove the prefix hazard, which is what the first
                // version of this comment claimed: both ids still begin with
                // `compare-progress`, so `[data-testid^="compare-progress"]`
                // would still collect both (/pr-review iter 2, Agent C N4). The
                // rename buys readability; killing that hazard would take a
                // different prefix entirely.
                <div data-testid="compare-progress-bar" className="flex flex-col gap-1">
                  <ProgressBar
                    value={runProgress.processed}
                    max={runProgress.total}
                    label="Compare progress"
                  />
                  {/* `aria-live` because `ProgressBar` is a labelled div rather
                      than a `role="progressbar"` (the sanctioned repo pattern —
                      biome flags the role as an interactive widget). Without it
                      a screen-reader user gets the same silence the old spinner
                      gave them, through the exact wait this feature exists to
                      make bearable (/pr-review iter 1, Agent A §6). */}
                  <span aria-live="polite" className="text-caption text-text-secondary">
                    {`Comparing… ${runProgress.processed} of ${runProgress.total}`}
                    {runProgress.label ? ` · ${runProgress.label}` : ""}
                  </span>
                </div>
              ) : (
                // No fraction to draw yet: the same shared bar, indeterminate,
                // so the panel keeps one shape from the first second to the last.
                <div className="flex flex-col gap-1">
                  <ProgressBar indeterminate live label="Comparing against Thrive" />
                  <span>Comparing this people list against Thrive…</span>
                </div>
              )}
            </div>
          ) : error ? (
            // SAFETY: error takes priority over `result` — a failed poll must
            // never fall through to the "No conflicts" success branch below.
            <div
              data-testid="compare-error"
              className="mt-5 rounded-md border-l-4 border-error-strong bg-error-muted p-4 text-body text-error"
            >
              <p className="font-semibold">Compare to Thrive couldn't finish.</p>
              <p className="mt-1 text-text-secondary">
                {error.message ||
                  "An unexpected error occurred while comparing this people list against Thrive."}
              </p>
              <p className="mt-2 text-caption text-text-secondary">
                We don't know yet whether this people list has conflicts with Thrive — retry before
                continuing.
              </p>
              {onRetry && (
                <Button
                  variant="secondary"
                  className="mt-3"
                  data-testid="compare-retry"
                  // THROUGH `handleRefresh`, not straight to `onRetry`.
                  //
                  // This button re-runs the compare exactly like the footer's
                  // does, so it needs the same bookkeeping: snapshot the
                  // questions, claim the job on screen as already reconciled,
                  // and let the reconciliation clear the optimistic overlays
                  // when the new verdict lands. Calling `onRetry` raw skipped
                  // all of it — a failed run followed by Retry left
                  // `celebratedIds` stale forever, and `verifiedClear`
                  // snapshotted a total those retired rows had deflated
                  // (/pr-review iter 2, Agent C, C2 — the half of the iter-1
                  // blocker its fix did not reach).
                  onClick={handleRefresh}
                >
                  Retry Compare
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* What the refresh cost the recruiter, in their terms. Renders
                  only when it cost something — a refresh that changed no
                  question should not open with a report about decisions. */}
              {refreshNotice && (
                <output
                  data-testid="compare-refresh-notice"
                  className="mb-4 block rounded-md border-l-4 border-warning-strong bg-warning-muted p-3 text-body text-text"
                >
                  {refreshNotice}
                </output>
              )}
              {/* Duplicate-LinkedIn warning banner — FIRST, before the stats
                  (approved 2026-09-07 mock): a write that will duplicate a URL
                  in Thrive is the one thing worth reading before anything
                  else. Renders only when at least one visible conflict
                  contact carries the firm-wide warning; each line anchors to
                  its card. */}
              {dupContacts.length > 0 && (
                <div
                  data-testid="compare-dup-warning-banner"
                  className="mt-5 rounded-md border-l-4 border-warning-strong bg-warning-muted p-4"
                >
                  <p className="text-body font-semibold text-warning-ink">
                    <span aria-hidden="true">⚠ </span>
                    {dupWarningTitle(dupContacts.length)}
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {dupContacts.map((c) => (
                      <li key={c.import_contact_id} className="text-caption text-text-secondary">
                        <a
                          href={`#conflict-card-${c.import_contact_id}`}
                          className="hover:underline"
                        >
                          {dupWarningLine(
                            [c.first_name, c.last_name].filter(Boolean).join(" ").trim() ||
                              "Contact",
                            c.contact_id_thrive
                          )}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Stats block — the SHARED component, so the Add panel cannot
                  describe the same run in different words. Every `compare-*`
                  testid below is produced by its default `testIdPrefix`. */}
              <CompareBucketList
                buckets={result?.buckets}
                actionLabel={actionLabel}
                isBct={isBct}
                headingSuffix={scopeNarrowed ? ` (${pendingCount} pending)` : ""}
              >
                {/* The ignored line closes the reconciliation: without it
                    the buckets above can sum short of the scope and nothing
                    says where the missing rows went (sub-task 11). Only
                    when at least one contact is ignored — a permanent
                    "0 ignored" row would be noise on the common path. */}
                {ignoredTotal > 0 && (
                  <li data-testid="compare-ignored-line" className="text-body text-text-secondary">
                    {ignoredStatsLine(ignoredTotal)}
                  </li>
                )}
              </CompareBucketList>

              {/* Warnings */}
              <section className="mt-6" data-testid="compare-warnings">
                {allConflictsHandledInView ? (
                  <p
                    data-testid="compare-conflicts-handled"
                    className="rounded-md border-l-4 border-warning-strong bg-surface p-3 text-body text-text-secondary"
                  >
                    {CONFLICTS_HANDLED_FOR_NOW}
                  </p>
                ) : hasConflicts ? (
                  <h3 className="text-h3 text-text">
                    {/* Counts EVERYTHING pending, across all four sections —
                        the same total the sections' own counts sum to. It used
                        to be `w1Count + w2Contacts.length`: visible-card counts
                        rather than the run's stale totals, which was the right
                        instinct, but it omitted W3 entirely, omitted the
                        different-people rows, and counted W2 cards the
                        recruiter had already ignored. */}
                    {pendingTotal} contact(s) need your attention before you continue:
                  </h3>
                ) : result?.inherited ? (
                  // A combined list's result is inherited from its source
                  // lists (no Compare ran on it), and conflict reviews do not
                  // carry over: "No conflicts" would claim a check that did
                  // not happen (epic 2026-10-01-combine-stability-perf).
                  <p
                    data-testid="compare-inherited-note"
                    className="rounded-md border-l-4 border-warning-strong bg-surface p-3 text-body text-text-secondary"
                  >
                    {inheritedResultNote(result.inherited)}
                  </p>
                ) : (
                  <p
                    data-testid="compare-no-conflicts"
                    className="rounded-md border-l-4 border-success bg-surface p-3 text-body text-success"
                  >
                    No conflicts — every contact can be added automatically.
                  </p>
                )}

                {/* TWO tabs: everything PENDING in one scroll, and `Ignored` —
                    the only finished thing — behind the second.

                    Tabs hide work, and hiding pending work is the failure the
                    operator named: you resolve what you can see, hit Continue,
                    and never learn another tab held items. Archive is the one
                    case where that does not apply. */}
                {hasConflicts && !allConflictsHandledInView && (
                  <>
                    <div
                      role="tablist"
                      aria-label="Conflicts"
                      className="mt-3 flex gap-1 border-b border-border"
                    >
                      {[
                        {
                          id: "needs_review" as const,
                          label: NEEDS_REVIEW_TAB_LABEL,
                          count: pendingTotal,
                          testId: "compare-tab-needs-review",
                        },
                        {
                          id: "ignored" as const,
                          label: IGNORED_TAB_LABEL,
                          count: ignoredTotal,
                          testId: "compare-tab-ignored",
                        },
                      ].map((tab) => (
                        <button
                          key={tab.id}
                          type="button"
                          role="tab"
                          aria-selected={activeTab === tab.id}
                          data-testid={tab.testId}
                          onClick={() => setActiveTab(tab.id)}
                          className={
                            activeTab === tab.id
                              ? "rounded-t-sm border-b-2 border-accent px-3 py-1.5 text-label font-semibold text-text"
                              : "rounded-t-sm border-b-2 border-transparent px-3 py-1.5 text-label text-text-secondary hover:text-text"
                          }
                        >
                          {tab.label} ({tab.count})
                        </button>
                      ))}
                    </div>

                    {/* ABOVE the empty/non-empty split, not inside the group.
                        The first Undo usually EMPTIES this tab — that is what
                        an undo does — and the note lived inside the group, so
                        it unmounted on the very render it existed for. */}
                    {activeTab === "ignored" && showUndoNote && (
                      <p
                        data-testid="compare-undo-recompare-note"
                        className="mt-3 rounded-sm border border-border bg-bg px-2.5 py-1.5 text-caption text-text-secondary"
                      >
                        {UNDO_NEEDS_RECOMPARE_NOTE}
                      </p>
                    )}
                    {/* THE DONE LIST. Above the pending sections, because
                    it answers "what have I accomplished" — a question
                    the recruiter asks while working, not after. It is
                    session-scoped and view-only; the durable record is
                    the contacts table. */}
                    {doneLog.length > 0 && (
                      <section data-testid="compare-done-list" className="mb-3">
                        <h4 className="text-h3 text-success-ink">
                          {DONE_SECTION_TITLE} ({doneLog.length})
                        </h4>
                        <ul className="mt-1.5 space-y-1">
                          {doneLog.map((entry) => (
                            <li
                              key={entry.id}
                              data-testid={`done-entry-${entry.id}`}
                              className="flex items-start gap-1.5 rounded-sm border border-success bg-success-muted px-2.5 py-1.5 text-label text-success-ink"
                            >
                              <span aria-hidden="true">✓</span>
                              <span>{entry.line}</span>
                            </li>
                          ))}
                        </ul>
                      </section>
                    )}
                    {activeTab === "ignored" ? (
                      ignoredTotal === 0 ? (
                        <p
                          data-testid="compare-ignored-empty"
                          className="mt-3 text-body text-text-secondary"
                        >
                          {IGNORED_TAB_EMPTY}
                        </p>
                      ) : (
                        <div data-testid="compare-ignored-group" className="mt-3 space-y-5">
                          {/* SAME GROUPING AS THE PENDING TAB, and each row
                              keeps the SHAPE it had there. A company change is
                              a one-line row on both sides; an identity conflict
                              is a card on both sides. Before this the tab was
                              one flat list of full cards, so dismissing a row
                              changed what it looked like — an archive that
                              re-renders its contents is hard to recognise. */}
                          {ignoredCompanyChanges.length > 0 && (
                            <section data-testid="compare-ignored-company-change-section">
                              <h4 className="text-h3 text-text">
                                {COMPANY_CHANGE_SECTION} ({ignoredCompanyChanges.length})
                              </h4>
                              <ul className="mt-2 space-y-1.5">
                                {ignoredCompanyChangePaged.items.map((c) => (
                                  <IgnoredCompanyChangeRow
                                    key={c.import_contact_id}
                                    contact={c}
                                    resolvedAvatarUrl={
                                      c.profile_image_s3_key
                                        ? resolvedAvatars.get(c.profile_image_s3_key)
                                        : undefined
                                    }
                                    onUndo={
                                      conflictActionsEnabled
                                        ? () => undoLeaveOut(c.import_contact_id)
                                        : undefined
                                    }
                                  />
                                ))}
                              </ul>
                              <ConflictPager
                                page={ignoredCompanyChangePaged.clamped}
                                total={ignoredCompanyChanges.length}
                                onPageChange={setIgnoredCompanyChangePage}
                                itemLabel="skipped updates for current role"
                              />
                            </section>
                          )}

                          {ignoredDecisions.length + priorOnly.length > 0 && (
                            <section data-testid="compare-ignored-decision-section">
                              <h4 className="text-h3 text-text">
                                {NEEDS_DECISION_SECTION} (
                                {ignoredDecisionItems.length + priorNotShown})
                              </h4>
                              <ul className="mt-2 space-y-3">
                                {ignoredDecisionPaged.items.map((item) => {
                                  // Rows dismissed BEFORE this run render
                                  // thinner: Compare never examined them, so
                                  // the conflict facts do not exist to show.
                                  if (item.kind === "row") return renderPriorIgnoredRow(item.row);
                                  const c = item.contact;
                                  const id = c.import_contact_id;
                                  return (
                                    <W2ConflictCard
                                      key={id}
                                      contact={c}
                                      action={actionFor(id)}
                                      actionError={actionErrors[id] ?? null}
                                      onActionChange={(next) => handleAction(c, next)}
                                      persistedActionsEnabled={conflictActionsEnabled}
                                      resolvedAvatarUrl={
                                        c.profile_image_s3_key
                                          ? resolvedAvatars.get(c.profile_image_s3_key)
                                          : undefined
                                      }
                                      leftOut
                                      leftOutAt={c.left_out_at}
                                      onUndoLeaveOut={
                                        conflictActionsEnabled ? () => undoLeaveOut(id) : undefined
                                      }
                                    />
                                  );
                                })}
                              </ul>
                              {priorNotShown > 0 && (
                                <p
                                  data-testid="compare-ignored-prior-truncated"
                                  className="mt-1 text-caption text-text-muted"
                                >
                                  {priorTruncatedNote(priorNotShown)}
                                </p>
                              )}
                              <ConflictPager
                                page={ignoredDecisionPaged.clamped}
                                // The COMBINED total, not one half of it. Handed
                                // `ignoredDecisions.length` the pager vanished
                                // whenever that list was empty — which is the
                                // common case, since Compare excludes
                                // previously-dismissed rows from scope.
                                total={ignoredDecisionItems.length}
                                onPageChange={setIgnoredDecisionPage}
                                itemLabel="ignored contacts"
                              />
                            </section>
                          )}

                          {/* SKIPPED FROM THE DUPLICATE-LINKEDIN SECTION.
                              Its own section, keeping the pending tab's name
                              and the card's own shape — the same rule the two
                              sections above follow. A row skipped here would
                              otherwise vanish from the pending scroll and be
                              filed nowhere, which is precisely the silent
                              disappearance this tab exists to prevent. */}
                          {skippedDifferentPeople.length > 0 && (
                            <section data-testid="compare-ignored-different-people-section">
                              <h4 className="text-h3 text-text">
                                {DIFFERENT_PEOPLE_SECTION} ({skippedDifferentPeople.length})
                              </h4>
                              <ul className="mt-2 space-y-3">
                                {skippedDifferentPeoplePaged.items.map((decision) => (
                                  <DifferentPersonCard
                                    key={decision.import_contact_id}
                                    decision={decision}
                                    resolvedAvatarUrl={
                                      decision.profile_image_s3_key
                                        ? resolvedAvatars.get(decision.profile_image_s3_key)
                                        : undefined
                                    }
                                    leftOut
                                    onUndoLeaveOut={
                                      conflictActionsEnabled
                                        ? () => undoLeaveOut(decision.import_contact_id)
                                        : undefined
                                    }
                                    error={undoErrors[decision.import_contact_id] ?? null}
                                  />
                                ))}
                              </ul>
                              <ConflictPager
                                page={skippedDifferentPeoplePaged.clamped}
                                total={skippedDifferentPeople.length}
                                onPageChange={setSkippedDifferentPeoplePage}
                                itemLabel="skipped contacts"
                              />
                            </section>
                          )}

                          {/* DISMISSED IN AN EARLIER SESSION, and shown with
                              less than the two sections above — on purpose.
                              Compare never re-examined these rows, and the
                              conflict facts (`names_match`, the Thrive-side
                              company) are not persisted per contact, so the
                              server cannot say which section they belonged to
                              or what employer they moved from. Rendering them
                              grouped would mean guessing.

                              They are here because the alternative was worse:
                              the tab used to read `Ignored (0)` over a real
                              dismissal, which tells the recruiter they never
                              ignored anyone. Fewer details and true beats
                              complete and absent. Restoring the full card is
                              a follow-up card — it needs the conflict facts
                              persisted at compare time. */}
                        </div>
                      )
                    ) : pendingTotal === 0 ? (
                      <p
                        data-testid="compare-needs-review-empty"
                        className="mt-3 text-body text-text-secondary"
                      >
                        {NEEDS_REVIEW_TAB_EMPTY}
                      </p>
                    ) : (
                      <div data-testid="compare-pending-scroll" className="mt-3 space-y-5">
                        {/* Chips NARROW a long list. Nothing is hidden by
                            default — that is the whole difference from the tabs
                            they replaced, and the reason `All` is selected. */}
                        {/* `fieldset` + `legend`, matching `ReviewStatusFilterBar`:
                            the repo's established shape for a chip row, and a
                            native grouping rather than `role="group"` on a div. */}
                        <fieldset className="flex min-w-0 flex-wrap items-center gap-1.5 border-0 p-0 m-0">
                          <legend className="sr-only">Filter pending conflicts</legend>
                          {[
                            { id: "all" as const, label: PENDING_FILTER_ALL, count: pendingTotal },
                            {
                              id: "company_change" as const,
                              label: COMPANY_CHANGE_SECTION,
                              count: companyChanges.length,
                            },
                            {
                              id: "needs_decision" as const,
                              label: NEEDS_DECISION_SECTION,
                              count: needsDecisionItems.length,
                            },
                            {
                              id: "different_people" as const,
                              label: DIFFERENT_PEOPLE_SECTION,
                              count: differentPeople.length,
                            },
                            {
                              id: "cant_resolve" as const,
                              label: FIX_IN_THRIVE_SECTION,
                              count: w1Groups.length,
                            },
                          ]
                            // A chip for a section with nothing in it would be a
                            // control that does nothing; `All` always renders.
                            .filter((chip) => chip.id === "all" || chip.count > 0)
                            .map((chip) => (
                              <button
                                key={chip.id}
                                type="button"
                                aria-pressed={pendingFilter === chip.id}
                                data-testid={`compare-pending-chip-${chip.id}`}
                                onClick={() => setPendingFilter(chip.id)}
                                className={
                                  pendingFilter === chip.id
                                    ? "rounded-full border border-accent bg-accent-muted px-3 py-1 text-label text-accent"
                                    : "rounded-full border border-border bg-surface px-3 py-1 text-label text-text-secondary hover:border-border-hover"
                                }
                              >
                                {chip.label} ({chip.count})
                              </button>
                            ))}
                        </fieldset>

                        {/* 1. COMPANY CHANGE — compact rows, pre-confirmed. */}
                        {showSection("company_change") && companyChanges.length > 0 && (
                          <section data-testid="compare-company-change-section">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex items-start gap-1">
                                <h4 className="text-h3 text-text">
                                  {COMPANY_CHANGE_SECTION} ({companyChanges.length})
                                </h4>
                                <InfoHint
                                  content={COMPANY_CHANGE_DETAIL}
                                  label="About updates for current role"
                                  escapesOverflow
                                />
                              </div>
                              <button
                                type="button"
                                data-testid="compare-company-change-confirm-all"
                                onClick={() => void confirmAllCompanyChanges()}
                                disabled={confirmingAll}
                                // Same hover as every other control here, and a
                                // DISABLED state while it runs: it fires one
                                // synchronous write per ticked row, so a button
                                // that stays live invites a second click that
                                // would duplicate the whole pass (operator,
                                // 2026-09-11).
                                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg px-2 py-1 text-label text-text hover:border-border-hover hover:bg-surface disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {confirmingAll && <Spinner size="sm" />}
                                {/* THE TICKED COUNT, not the total (operator, 2026-09-11).
    The button said "Confirm all 2" and then confirmed
    one, because it counted the rows on screen while the
    handler acted on the ticked ones. Two readings of the
    same set, and the label was the wrong one — it is a
    promise about what the click will do. */}
                                {confirmAllLabel(tickedCompanyChanges.length)}
                              </button>
                            </div>
                            <p className="mt-1 text-caption text-text-secondary">
                              {COMPANY_CHANGE_LEAD}
                            </p>
                            {confirmAllNeedsCompareCount > 0 && (
                              <p
                                data-testid="compare-company-change-needs-compare"
                                className="mt-1 text-body-sm text-warning-ink"
                              >
                                <span aria-hidden="true">⚠ </span>
                                {confirmAllNeedsCompareLine(confirmAllNeedsCompareCount)}
                              </p>
                            )}
                            <ul className="mt-2 space-y-1.5">
                              {companyChangePaged.items.map((c) => (
                                <CompanyChangeRow
                                  key={c.import_contact_id}
                                  contact={c}
                                  confirmed={!unconfirmedCompanyChanges.has(c.import_contact_id)}
                                  onConfirmedChange={(next) =>
                                    setCompanyChangeConfirmed(c.import_contact_id, next)
                                  }
                                  resolvedAvatarUrl={
                                    c.profile_image_s3_key
                                      ? resolvedAvatars.get(c.profile_image_s3_key)
                                      : undefined
                                  }
                                  // The SAME action the conflict cards route
                                  // through `handleAction`, reached directly
                                  // because this row has no SegmentedControl to
                                  // carry it. One mechanism, two entry points.
                                  onIgnore={() => handleAction(c, "removeFromList")}
                                />
                              ))}
                            </ul>
                            <ConflictPager
                              page={companyChangePaged.clamped}
                              total={companyChanges.length}
                              onPageChange={setCompanyChangePage}
                              itemLabel="updates for current role"
                            />
                          </section>
                        )}

                        {/* 2. NEEDS YOUR DECISION — the real identity
                            conflicts plus every W3. Full cards, four actions. */}
                        {showSection("needs_decision") && needsDecisionItems.length > 0 && (
                          <section data-testid="compare-needs-decision-section">
                            <h4 className="text-h3 text-text">
                              {NEEDS_DECISION_SECTION} ({needsDecisionItems.length})
                            </h4>
                            <ul className="mt-2 space-y-3">
                              {needsDecisionPaged.items.map((c) => {
                                const id = c.import_contact_id;
                                return (
                                  <W2ConflictCard
                                    key={id}
                                    contact={c}
                                    // The write's outcome, on the PENDING side
                                    // only — the skipped tab is an archive and a
                                    // dismissed row has no write to report.
                                    addStatus={addStatusNode(id)}
                                    // A row reaches this section as a W2 only
                                    // when its NAME differs; every W3 is here by
                                    // definition. `names_match` decides the
                                    // section, `is_w3` membership decides the
                                    // card's copy and its proceed arm.
                                    kind={w3Active.includes(c) ? "w3" : "w2"}
                                    action={actionFor(id)}
                                    actionError={actionErrors[id] ?? null}
                                    onActionChange={(next) => handleAction(c, next)}
                                    persistedActionsEnabled={conflictActionsEnabled}
                                    resolvedAvatarUrl={
                                      c.profile_image_s3_key
                                        ? resolvedAvatars.get(c.profile_image_s3_key)
                                        : undefined
                                    }
                                  />
                                );
                              })}
                            </ul>
                            <ConflictPager
                              page={needsDecisionPaged.clamped}
                              total={needsDecisionItems.length}
                              onPageChange={setNeedsDecisionPage}
                              itemLabel="decisions"
                            />
                          </section>
                        )}

                        {/* 3. DIFFERENT PEOPLE — decided, NOT finished. */}
                        {showSection("different_people") && differentPeople.length > 0 && (
                          <section data-testid="compare-different-people-section">
                            <h4 className="text-h3 text-text">
                              {DIFFERENT_PEOPLE_SECTION} ({differentPeople.length})
                            </h4>
                            <ul className="mt-2 space-y-3">
                              {differentPeoplePaged.items.map((decision) => {
                                const dpId = decision.import_contact_id;
                                const verdict = recheckOutcomes[dpId] ?? null;
                                return (
                                  <DifferentPersonCard
                                    key={dpId}
                                    decision={decision}
                                    resolvedAvatarUrl={
                                      decision.profile_image_s3_key
                                        ? resolvedAvatars.get(decision.profile_image_s3_key)
                                        : undefined
                                    }
                                    error={undoErrors[dpId] ?? null}
                                    onUndo={
                                      conflictActionsEnabled
                                        ? (thriveId) => undoDifferentPerson(dpId, thriveId)
                                        : undefined
                                    }
                                    action={duplicateDecisions[dpId] ?? null}
                                    // No api/importId → no options at all. Both
                                    // of them are server calls; rendering rows
                                    // that cannot do anything would be the
                                    // disabled-with-an-explanation shape this
                                    // epic already removed once.
                                    onActionChange={
                                      conflictActionsEnabled
                                        ? (next) => handleDuplicateLinkedInAction(dpId, next)
                                        : undefined
                                    }
                                    recheckPending={recheckPendingIds.has(dpId)}
                                    recheckOutcome={verdict?.outcome ?? null}
                                    shakeKey={verdict?.attempt ?? 0}
                                    recheckDetail={verdict?.detail ?? null}
                                    // FIRED FROM THE RESPONSE, never from the
                                    // handler — the reward belongs to the fix
                                    // working, not to a radio being clicked —
                                    // and only behind `?gamification=off`
                                    // (Trello #790, reviewed 2026-09-15).
                                    // The slot now reports the ADD, not the
                                    // re-check: the re-check only says the
                                    // collision is gone, and "Added to the
                                    // search" is a claim about a write.
                                    celebration={addStatusNode(dpId)}
                                  />
                                );
                              })}
                            </ul>
                            <ConflictPager
                              page={differentPeoplePaged.clamped}
                              total={differentPeople.length}
                              onPageChange={setDifferentPeoplePage}
                              itemLabel="decisions"
                            />
                          </section>
                        )}

                        {/* 4. CAN'T BE RESOLVED HERE — W1. */}
                        {showSection("cant_resolve") && w1Groups.length > 0 && (
                          <section data-testid="compare-cant-resolve-section">
                            <h4 className="text-h3 text-conflict">
                              {FIX_IN_THRIVE_SECTION} ({w1Groups.length})
                            </h4>
                            <ul className="mt-2 space-y-3">
                              {cantResolvePaged.items.map((group) => {
                                const first = group.contacts?.[0];
                                // ONE card per import ROW, so the decision is
                                // keyed on the row — every member of a W1
                                // group shares this id by construction.
                                const w1Id = first?.import_contact_id ?? null;
                                return (
                                  <W1ConflictCard
                                    key={group.key}
                                    group={group}
                                    resolvedAvatarUrl={
                                      first?.profile_image_s3_key
                                        ? resolvedAvatars.get(first.profile_image_s3_key)
                                        : undefined
                                    }
                                    action={w1Id ? (duplicateDecisions[w1Id] ?? null) : null}
                                    // `thrive_duplicates`, NOT the default. The
                                    // recruiter was asked to MERGE, and a merge
                                    // leaves one contact — under the stricter
                                    // rule that reads as an unresolved collision
                                    // and shakes at them for doing it right.
                                    onActionChange={
                                      conflictActionsEnabled && w1Id
                                        ? (next) =>
                                            handleDuplicateLinkedInAction(
                                              w1Id,
                                              next,
                                              "thrive_duplicates"
                                            )
                                        : undefined
                                    }
                                    recheckPending={w1Id ? recheckPendingIds.has(w1Id) : false}
                                    recheckOutcome={
                                      w1Id ? (recheckOutcomes[w1Id]?.outcome ?? null) : null
                                    }
                                    shakeKey={w1Id ? (recheckOutcomes[w1Id]?.attempt ?? 0) : 0}
                                    celebration={w1Id ? addStatusNode(w1Id) : undefined}
                                  />
                                );
                              })}
                            </ul>
                            <ConflictPager
                              page={cantResolvePaged.clamped}
                              total={w1Groups.length}
                              onPageChange={setCantResolvePage}
                              itemLabel="conflicts"
                            />
                          </section>
                        )}
                      </div>
                    )}
                  </>
                )}
              </section>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border p-5">
          {/* THE DISABLED STATE NAMES THE COUNT, and it is visible rather than
              only in a tooltip: a primary button that silently refuses to
              respond is the failure mode a disabled control usually IS, and
              "Continue is off" without "3 rows remain" gives the recruiter
              nothing to act on. It renders beside the button rather than under
              a card, because the button is where they discover the block.

              The rows still owing an answer are the ones with no highlight
              fill — which is why the selected state had to be a fill and not a
              pressed state for this line to be usable at all. */}
          {undecidedCount > 0 && !nothingToCompare && (
            // WARNING TONE, ICON INCLUDED (operator, 2026-09-15). It read as
            // secondary grey caption — the tone this app uses for things that
            // do not matter — while saying that contacts are about to be left
            // out of the search. Same treatment as the duplicate-LinkedIn
            // banner twenty lines up, so the two warnings in this modal cannot
            // look like different kinds of thing.
            //
            // THE GLYPH SITS OUTSIDE THE TESTID, deliberately. Two suites pin
            // this sentence as a LITERAL (a web unit test and
            // `compare-states.spec.ts`, which is the pairing that makes a copy
            // rename fail in seconds rather than in a 13-minute run). Putting
            // decoration inside the asserted node would have made both of them
            // assert the icon too, and the next tone change would read as a
            // copy change.
            <span className="mr-auto flex items-center gap-1.5 text-caption font-semibold text-warning-ink">
              <span aria-hidden="true">⚠</span>
              <span data-testid="compare-continue-blocked">
                {undecidedBlockerLine(undecidedCount)}
              </span>
            </span>
          )}
          <Button
            variant="secondary"
            onClick={() => onClose(effectiveDecisions)}
            data-testid="compare-close"
          >
            Close
          </Button>
          {/* Nothing pending to act on for this selection → no Continue. Compare
              still never hard-blocks Export elsewhere (FR-13): the page's own
              Export CTAs are unaffected by this modal's empty-scope state. */}
          {/* THE PRIMARY ACTION FOLLOWS THE WORK, not the other way round
              (operator, 2026-09-14). While anything is still pending, the one
              thing worth offering is another look — the recruiter's loop is
              read a conflict, go fix Thrive, come back, and until this the
              coming back was a modal close and a fresh Compare. Only when the
              list comes back empty does the modal offer the export.

              THIS IS NOT THE GATE fu5 REMOVED, and the difference is where the
              exit lives. The old gate DISABLED this button and left the
              recruiter holding twenty answered rows with nowhere to take them.
              Here the page's own `Add to search` stays live the moment a
              compare has run (Trello #758 owns that rule) and skips whatever is
              still to-review — so a recruiter who decides to leave three rows
              unresolved closes the modal and exports the other fourteen. The
              modal simply stops SUGGESTING it while there is work on screen.

              Falls back to the export CTA when no re-run is wired: a surface
              with no `onRetry` (isolated renders) must still have a way
              forward.

              `isRunning` FIRST, and it is not cosmetic. A run in flight
              publishes a `running` document with EMPTY conflicts, so any count
              read off it is a placeholder rather than an answer, and the footer
              flipped for a second or two before the real verdict landed — the
              button changing its mind while the recruiter watched (operator,
              2026-09-15).

              `verifiedClear` carries the rest. See its declaration for the two
              screen-derived conditions it replaced, and how each one let the
              button announce a verification nobody performed. */}
          {!nothingToCompare && (isRunning || !verifiedClear) && onRetry ? (
            <Button
              variant="primary"
              data-testid="compare-refresh"
              onClick={handleRefresh}
              disabled={isRunning}
            >
              {isRunning ? RERUN_COMPARE_RUNNING_LABEL : RERUN_COMPARE_LABEL}
            </Button>
          ) : !nothingToCompare ? (
            <Button
              variant="primary"
              data-testid="compare-continue"
              title={undecidedCount > 0 ? undecidedBlockerLine(undecidedCount) : undefined}
              onClick={() => onContinueToExport(effectiveDecisions)}
            >
              {/* Names the DESTINATION, and takes it from the page rather
                  than deciding it here. The literals this replaces were the
                  synthesis of two review findings from opposite sides —
                  "Continue to Export →" used the verb #743 retired, and a bare
                  "Add" reads as an infinitive missing its object — so each
                  branch named its own destination and matched
                  `addAction.label`, NOT `selectedLabel`, which carries the word
                  "Selected" and belongs to the footer CTA.
                  The cost that comment recorded is now paid: it said "these
                  literals are a hand-kept copy of a `const` in another file —
                  rename `addAction.label` and nothing here goes red", and
                  pointed at `ExportToThrivePanel`'s `actionLabel` prop as the
                  fix. That prop now exists here too, so there is one object and
                  no copy to keep. */}
              Continue to {actionLabel} →
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
